// Theme configuration for Malachite components
export type ThemeMode = 'light' | 'dark';

export interface MalachiteTheme {
    fontSizes: {
        xsmall: string;
        small: string;
        medium: string;
        /** Off-ladder: the control ladder's large tier is 15px, between `medium` and `large`. */
        '15'?: string;
        large: string;
        xlarge: string;
        xxlarge: string;
    };
    /**
     * Heights of the shared control ladder. Optional so adding the family never breaks a
     * consumer building a full theme literal — same reason `radius.xs` is optional.
     */
    controlHeights?: {
        small: string;
        medium: string;
        large: string;
    };
    iconSizes: {
        xsmall: string;
        small: string;
        medium: string;
        large: string;
    };
    radius: {
        /** Optional so adding a rung never breaks a consumer building a full theme literal. */
        xs?: string;
        sm: string;
        md: string;
        lg: string;
        /** Off-ladder: no rung exists between `lg` (8px) and `xl` (12px). */
        '10'?: string;
        xl: string;
        xxl: string;
        xxxl: string;
    };
    shadows: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
        default: string;
        glow?: string;
        glowLg?: string;
        /** Optional so adding a shadow never breaks a consumer building a full theme literal. */
        accent?: string;
        success: string;
        warning: string;
        error: string;
    };
    colors: {
        shadowColor: string;
        shadowOpacity: number;
        card: {
            background: string;
            foreground: string;
            border: string;
            shadow: string;
            dimmedBackground: string;
            sunkenBackground: string;
            warmBackground: string;
            headerBackground: string;
            footerBackground: string;
            /** @deprecated No longer read by any component — glass surfaces derive from `sunkenBackground` (see `glassSurface.ts`). */
            footerGlassBackground: string;
            trayNeutral?: string;
            trayWarning?: string;
            trayError?: string;
            traySuccess?: string;
            subtleDivider?: string;
        };
        overlay: {
            /** Dimming layer drawn behind modal overlays (e.g. SidePanel). */
            scrim: string;
        };
        alert: {
            info: {
                background: string;
                foreground: string;
            };
            success: {
                background: string;
                foreground: string;
            };
            error: {
                background: string;
                foreground: string;
            };
            warning: {
                background: string;
                foreground: string;
            };
            /** Says a machine wrote this, not that anything is wrong — the violet the
             *  assistant is named in elsewhere, not a fifth severity. */
            assistant: {
                background: string;
                foreground: string;
            };
        };
        tooltip: {
            background: string;
            foreground: string;
            border: string;
            shadow: string;
        };
        primary: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
        };
        /** Brand accent for interaction states — checked, selected, active. Green today,
         *  but a rebrand repaints it; `success.*` stays status-only and does not follow.
         *  Optional so adding a group never breaks a consumer building a full theme literal. */
        accent?: {
            background: string;
            foreground: string;
            border: string;
            backgroundGlass: string;
        };
        control: {
            borderHover: string;
        };
        outline: {
            foreground: string;
        };
        secondary: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
        };
        destructive: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
            shadow: string;
            shadowLight: string;
            shadowMedium: string;
        };
        success: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
            backgroundGlass: string;
            shadowLight: string;
            shadowMedium: string;
        };
        warning: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
            backgroundGlass: string;
            shadowLight: string;
            shadowMedium: string;
        };
        error: {
            background: string;
            foreground: string;
            border: string;
            borderHover: string;
            backgroundGlass: string;
            shadowLight: string;
            shadowMedium: string;
        };
        buttonList: {
            background: string;
            foreground: string;
            hoverBackground: string;
            hoverForeground: string;
        };
        pillButton: {
            background: string;
            foreground: string;
            hoverBackground: string;
            hoverForeground: string;
            pressedBackground: string;
            pressedForeground: string;
            pressedOpacity: string;
            activeBackground: string;
            activeForeground: string;
            staticBackground: string;
            staticForeground: string;
            invertedBackground: string;
            invertedForeground: string;
            invertedHoverBackground: string;
            invertedHoverForeground: string;
            invertedPressedBackground: string;
            invertedPressedForeground: string;
        };
        toggle: {
            track: string;
            trackChecked: string;
            /** The checked track while the change it represents has not been saved yet. */
            trackCheckedPending: string;
            knob: string;
        };
        segmentedControl: {
            trackBackground: string;
            itemForeground: string;
            itemHoverForeground: string;
            selectedBackground: string;
            selectedForeground: string;
            selectedShadow: string;
        };
        selectableCard: {
            background: string;
            border: string;
            selectedTint: string;
            selectedSurfaceEnd: string;
            selectedBorder: string;
            librarySelectedTint: string;
            librarySelectedBorder: string;
            editingTint: string;
            accent: string;
            mutedForeground: string;
            hoverBackground: string;
            hoverRule: string;
            pendingForeground: string;
        };
        cardStrip: {
            background: string;
            divider: string;
            fade: string;
            arrowBackground: string;
            arrowBorder: string;
            addPrimaryBackground: string;
            addPrimaryForeground: string;
        };
        selectionBar: {
            background: string;
            foreground: string;
            mutedForeground: string;
            divider: string;
            border: string;
            primaryBorder: string;
            shadow: string;
            noticeBackground: string;
            noticeForeground: string;
            noticeShadow: string;
        };
        inlineConfirm: {
            background: string;
            foreground: string;
            divider: string;
            shadow: string;
        };
        dropdown: {
            border: string;
            borderActive: string;
            borderFilter: string;
            borderError: string;
            borderPending: string;
            shadow: string;
            shadowHover: string;
            shadowActive: string;
            shadowFilter: string;
            shadowError: string;
            shadowPending: string;
            disabledBackground: string;
        };
        tag: {
            primary: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            secondary: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            default: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            error: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            warning: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            success: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            emphasis: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            info: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
            assistant: {
                background: string;
                foreground: string;
                border: string;
                shadow: string;
            };
        };
        numberTag: {
            background: string;
            foreground: string;
            emphasisBackground: string;
            emphasisForeground: string;
        };
        table: {
            background: string;
            boxShadow: string;
            header: {
                background: string;
                foreground: string;
                border: string;
            };
            row: {
                background: string;
                foreground: string;
                hoverBackground: string;
                border: string;
                /** `Table`'s `selectedRowKey` tint. Must stay distinguishable from
                 *  `hoverBackground`: a selected row keeps this tint under the cursor. */
                selectedBackground: string;
                /** Leading accent bar on that same row. */
                selectedAccent: string;
                /** Cascades, so cells setting their own colour are deliberately unaffected. */
                selectedForeground: string;
            };
            border: string;
            borderedRow: {
                background: string;
                outline: string;
            };
            rowVariant: {
                success: {
                    background: string;
                };
                warning: {
                    background: string;
                };
                error: {
                    background: string;
                };
                info: {
                    background: string;
                };
            };
            error: {
                iconBackground: string;
                iconForeground: string;
                titleForeground: string;
                subtitleForeground: string;
            };
        };
        text: {
            primary: string;
            secondary: string;
            dimmed: string;
            emphasis: string;
            muted: string;
            placeholder: string; // Figma #bbbbbb for input placeholders
            destructive: string;
            success: string;
            highlight: string;
            warning: string;
            warningMuted: string;
            error: string;
            info: string;
            linkForeground: string;
            linkForegroundHover: string;
            rightSideText: {
                muted: string; // #7B7B7B for less prominent right side text
                primary: string; // #0A0A0A for more prominent right side text
                alignment: 'left' | 'right'; // Alignment of right side text
                fontSize: string; // Font size of right side text
                minWidth: string; // Width the column reserves, so short values stay aligned
            };
        };
        codeBlock: {
            background: string;
            foreground: string;
            border: string;
            lineNumber: string;
            keyword: string; // for keywords like 'unified', 'string', etc.
            comment: string; // for comments
            string: string; // for string literals
            number: string; // for numbers
            type: string; // for type annotations
            diffAddedBackground: string; // tint for a line a diff added
            diffRemovedBackground: string; // tint for a line a diff removed
            diffAssistantBackground: string; // tint for an added line in the assistant diff palette
            diffAssistantForeground: string; // marker colour for an added line in the assistant diff palette
            diffAssistantRemovedBackground: string; // tint for a removed line in the assistant diff palette
            diffRemovedForeground: string; // marker colour for a removed line in the assistant diff palette
            diffContextForeground: string; // marker colour for an unchanged line in the assistant diff palette
            diffContextText: string; // text colour for an unchanged line in the assistant diff palette
        };
        /** The conversation's own surfaces — message bubbles, not chrome around them. */
        chat: {
            userBubbleBackground: string;
            userBubbleBorder: string;
            assistantBubbleBackground: string;
            assistantBubbleBorder: string;
            assistantBubbleForeground: string;
        };
        /** Chrome marking machine-authored content outside the conversation itself
         *  (badges, diff callouts) — distinct from `chat`, which is the conversation's
         *  own bubbles. */
        assistant: {
            foreground: string;
            surfaceBackground: string;
            surfaceBorder: string;
            /** Body text sitting on `surfaceBackground` — darker/muted so a run of it reads,
             *  unlike `foreground`, which is the vivid accent. */
            surfaceForeground: string;
        };
    };
}

// Define a type for partial theme updates
export interface PartialMalachiteTheme {
    fontSizes?: Partial<MalachiteTheme['fontSizes']>;
    iconSizes?: Partial<MalachiteTheme['iconSizes']>;
    radius?: Partial<MalachiteTheme['radius']>;
    controlHeights?: Partial<MalachiteTheme['controlHeights']>;
    shadows?: Partial<MalachiteTheme['shadows']>;
    colors?: {
        shadowColor?: string;
        shadowOpacity?: number;
        card?: Partial<MalachiteTheme['colors']['card']>;
        overlay?: Partial<MalachiteTheme['colors']['overlay']>;
        /** Nested-partial: a sub-object may be overridden leaf by leaf. A flat
         *  `Partial<…>` would demand every leaf of any group you touch. */
        alert?: {
            [K in keyof MalachiteTheme['colors']['alert']]?: Partial<
                MalachiteTheme['colors']['alert'][K]
            >;
        };
        tooltip?: Partial<MalachiteTheme['colors']['tooltip']>;
        primary?: Partial<MalachiteTheme['colors']['primary']>;
        accent?: Partial<NonNullable<MalachiteTheme['colors']['accent']>>;
        control?: Partial<MalachiteTheme['colors']['control']>;
        outline?: Partial<MalachiteTheme['colors']['outline']>;
        secondary?: Partial<MalachiteTheme['colors']['secondary']>;
        destructive?: Partial<MalachiteTheme['colors']['destructive']>;
        success?: Partial<MalachiteTheme['colors']['success']>;
        warning?: Partial<MalachiteTheme['colors']['warning']>;
        error?: Partial<MalachiteTheme['colors']['error']>;
        buttonList?: Partial<MalachiteTheme['colors']['buttonList']>;
        pillButton?: Partial<MalachiteTheme['colors']['pillButton']>;
        toggle?: Partial<MalachiteTheme['colors']['toggle']>;
        segmentedControl?: Partial<MalachiteTheme['colors']['segmentedControl']>;
        selectableCard?: Partial<MalachiteTheme['colors']['selectableCard']>;
        cardStrip?: Partial<MalachiteTheme['colors']['cardStrip']>;
        selectionBar?: Partial<MalachiteTheme['colors']['selectionBar']>;
        inlineConfirm?: Partial<MalachiteTheme['colors']['inlineConfirm']>;
        dropdown?: Partial<MalachiteTheme['colors']['dropdown']>;
        tag?: {
            [K in keyof MalachiteTheme['colors']['tag']]?: Partial<
                MalachiteTheme['colors']['tag'][K]
            >;
        };
        numberTag?: Partial<MalachiteTheme['colors']['numberTag']>;
        // Every nested group is listed: a flat Partial<> reaches only the top level, and any
        // group omitted here is shallow-spread by mergeThemes, silently dropping siblings.
        table?: Partial<
            Omit<
                MalachiteTheme['colors']['table'],
                'header' | 'row' | 'borderedRow' | 'rowVariant' | 'error'
            >
        > & {
            header?: Partial<MalachiteTheme['colors']['table']['header']>;
            row?: Partial<MalachiteTheme['colors']['table']['row']>;
            borderedRow?: Partial<MalachiteTheme['colors']['table']['borderedRow']>;
            // Two levels: each variant is its own object.
            rowVariant?: {
                [K in keyof MalachiteTheme['colors']['table']['rowVariant']]?: Partial<
                    MalachiteTheme['colors']['table']['rowVariant'][K]
                >;
            };
            error?: Partial<MalachiteTheme['colors']['table']['error']>;
        };
        text?: Partial<Omit<MalachiteTheme['colors']['text'], 'rightSideText'>> & {
            rightSideText?: Partial<MalachiteTheme['colors']['text']['rightSideText']>;
        };
        codeBlock?: Partial<MalachiteTheme['colors']['codeBlock']>;
        chat?: Partial<MalachiteTheme['colors']['chat']>;
        assistant?: Partial<MalachiteTheme['colors']['assistant']>;
    };
}

// `accent` is optional on MalachiteTheme, so a hand-built theme may omit it. Every read
// site falls back to these rather than emitting `undefined` or throwing.
const DEFAULT_ACCENT_COLORS: NonNullable<MalachiteTheme['colors']['accent']> = {
    background: 'rgba(0, 175, 102)',
    foreground: 'rgb(2, 122, 66)',
    border: 'rgba(0, 175, 102, 0.2)',
    backgroundGlass: 'rgba(0, 175, 102, 0.08)',
};
const DEFAULT_ACCENT_SHADOW = '0px 1px 0px 0px rgba(0, 175, 102, 0.15)';

/** The shared control ladder, from Figma. Exported so callers can read it without
 *  reaching for a possibly-absent theme field. */
export const CONTROL_HEIGHT = {
    small: '28px',
    medium: '32px',
    large: '42px',
} as const;

// Default theme values
export const defaultTheme: MalachiteTheme = {
    fontSizes: {
        xsmall: '11px',
        small: '13px',
        medium: '14px',
        '15': '15px',
        large: '16px',
        xlarge: '18px',
        xxlarge: '22px',
    },
    controlHeights: { ...CONTROL_HEIGHT },
    iconSizes: {
        xsmall: '12px',
        small: '13px',
        medium: '13px',
        large: '15px',
    },
    radius: {
        xs: '2px',
        sm: '4px',
        md: '6px',
        lg: '8px',
        '10': '10px',
        xl: '12px',
        xxl: '14px',
        xxxl: '16px',
    },
    shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        glow: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
        glowLg: '0px 0px 20px 0px rgba(0, 0, 0, 0.2)',
        default: '0px 1px 0px 0px rgba(0, 0, 0, 0.15)',
        accent: DEFAULT_ACCENT_SHADOW,
        success: '0px 1px 0px 0px rgba(0, 175, 102, 0.15)',
        warning: '0px 1px 0px 0px rgba(241, 139, 10, 0.15)',
        error: '0px 1px 0px 0px rgba(197, 63, 63, 0.15)',
    },
    colors: {
        shadowColor: '0, 0, 0',
        shadowOpacity: 0.1,
        card: {
            background: 'rgb(255, 255, 255)',
            foreground: 'rgb(10, 10, 10)',
            border: 'rgb(232, 232, 232)',
            shadow: 'rgba(0, 0, 0, 0.1)',
            dimmedBackground: 'rgb(247, 247, 247)',
            sunkenBackground: 'rgb(250, 250, 250)',
            warmBackground: 'rgb(244, 242, 240)',
            headerBackground: 'rgb(255, 255, 255)',
            footerBackground: 'rgb(255, 255, 255)',
            footerGlassBackground: 'rgba(255, 255, 255, 0.7)',
            trayNeutral: 'rgb(247, 247, 247)', // Figma #F7F7F7
            trayWarning: 'rgb(253, 243, 230)', // Figma #FDF3E6
            trayError: 'rgb(254, 241, 241)', // Figma #FEF1F1
            traySuccess: 'rgb(236, 249, 244)', // Figma #ECF9F4
            subtleDivider: 'rgb(241, 241, 241)', // Figma #F1F1F1
        },
        overlay: {
            scrim: 'rgba(0, 0, 0, 0.1)',
        },
        alert: {
            info: {
                background: 'rgb(247, 247, 247)',
                foreground: 'rgb(10, 10, 10)',
            },
            success: {
                background: 'rgba(0, 175, 102, 0.08)',
                foreground: 'rgb(2, 122, 66)',
            },
            error: {
                background: 'rgba(197, 63, 63, 0.08)',
                foreground: 'rgb(237, 53, 54)',
            },
            warning: {
                background: 'rgba(241, 139, 10, 0.08)',
                foreground: 'rgb(150, 88, 10)',
            },
            assistant: {
                background: 'rgb(242, 238, 254)', // #F2EEFE, the assistant tag's ground
                foreground: 'rgb(110, 86, 207)', // #6E56CF
            },
        },
        tooltip: {
            background: 'rgb(59, 59, 59)',
            foreground: 'rgb(255, 255, 255)',
            border: 'rgb(60, 60, 60)',
            shadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.20)',
        },
        primary: {
            background: 'rgb(48, 48, 48)',
            foreground: 'rgb(10, 10, 10)',
            border: 'rgb(232, 232, 232)',
            borderHover: 'rgb(186, 186, 186)',
        },
        accent: DEFAULT_ACCENT_COLORS,
        control: {
            borderHover: 'rgba(0, 175, 102, 0.4)',
        },
        outline: {
            foreground: 'rgb(10, 10, 10)',
        },
        secondary: {
            background: 'rgb(9, 9, 9)',
            foreground: 'rgb(255, 255, 255)',
            border: 'rgb(232, 232, 232)',
            borderHover: 'rgb(186, 186, 186)',
        },
        destructive: {
            background: 'transparent',
            foreground: 'rgb(196, 62, 62)',
            border: 'rgb(239, 55, 55)',
            borderHover: 'rgb(239, 55, 55)',
            shadow: 'rgb(239, 55, 55)',
            shadowLight: 'rgba(239, 55, 55, 0.4)',
            shadowMedium: 'rgba(239, 55, 55, 0.6)',
        },
        success: {
            background: 'rgba(0, 175, 102)',
            foreground: 'rgb(2, 122, 66)',
            border: 'rgba(0, 175, 102, 0.2)',
            borderHover: 'rgba(0, 175, 102, 0.4)',
            backgroundGlass: 'rgba(0, 175, 102, 0.08)',
            shadowLight: 'rgba(0, 175, 102, 0.4)',
            shadowMedium: 'rgba(0, 175, 102, 0.6)',
        },
        warning: {
            background: 'rgb(241, 139, 10)',
            foreground: 'rgb(150, 88, 10)',
            border: 'rgba(241, 139, 10, 0.2)',
            borderHover: 'rgba(241, 139, 10, 0.4)',
            backgroundGlass: 'rgba(241, 139, 10, 0.08)',
            shadowLight: 'rgba(241, 139, 10, 0.4)',
            shadowMedium: 'rgba(241, 139, 10, 0.6)',
        },
        error: {
            background: 'rgb(197, 63, 63)',
            foreground: 'rgb(237, 53, 54)',
            border: 'rgba(197, 63, 63, 0.2)',
            borderHover: 'rgba(197, 63, 63, 0.4)',
            backgroundGlass: 'rgba(197, 63, 63, 0.08)',
            shadowLight: 'rgba(197, 63, 63, 0.4)',
            shadowMedium: 'rgba(197, 63, 63, 0.6)',
        },
        buttonList: {
            background: 'rgb(255, 255, 255)',
            foreground: 'rgb(10, 10, 10)',
            hoverBackground: 'rgb(245, 245, 245)',
            hoverForeground: 'rgb(10, 10, 10)',
        },
        pillButton: {
            background: 'rgb(241, 241, 241)', // Figma #f1f1f1
            foreground: 'rgb(123, 123, 123)', // Figma #7b7b7b
            hoverBackground: 'rgb(232, 232, 232)', // Figma #e8e8e8
            hoverForeground: 'rgb(10, 10, 10)', // Figma neutral-950 (almost black)
            pressedBackground: 'rgb(232, 232, 232)', // Figma #e8e8e8 (same as hover)
            pressedForeground: 'rgb(10, 10, 10)', // Figma neutral-950 (same as hover)
            pressedOpacity: '0.6', // Figma opacity-60
            activeBackground: 'rgb(48, 48, 48)',
            activeForeground: 'rgb(255, 255, 255)',
            staticBackground: 'rgb(224, 222, 220)', // #e0dedc — used by interactive=false pills
            staticForeground: 'rgb(76, 75, 75)', // #4c4b4b
            invertedBackground: 'rgb(255, 255, 255)',
            invertedForeground: 'rgb(76, 75, 75)', // #4c4b4b
            invertedHoverBackground: 'rgb(10, 10, 10)', // neutral-950
            invertedHoverForeground: 'rgb(255, 255, 255)',
            invertedPressedBackground: 'rgb(10, 10, 10)',
            invertedPressedForeground: 'rgb(255, 255, 255)',
        },
        toggle: {
            track: 'rgb(232, 232, 232)', // #e8e8e8 neutral off track
            trackChecked: 'rgb(0, 175, 102)', // #00AF66 brand green
            trackCheckedPending: 'rgb(241, 139, 10)', // #F18B0A, the pending amber
            knob: 'rgb(255, 255, 255)',
        },
        segmentedControl: {
            trackBackground: 'rgb(241, 241, 241)', // Figma #f1f1f1 sunken track
            // Deliberately text.muted, not text.secondary: #7b7b7b on the #f1f1f1 track is
            // only 3.7:1, below WCAG AA for body text. #4c4b4b is 7.7:1.
            itemForeground: 'rgb(76, 75, 75)', // #4c4b4b text.muted
            itemHoverForeground: 'rgb(10, 10, 10)', // neutral-950
            selectedBackground: 'rgb(255, 255, 255)',
            selectedForeground: 'rgb(10, 10, 10)', // neutral-950
            selectedShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.1)',
        },
        selectableCard: {
            background: 'rgb(241, 241, 241)',
            border: 'rgb(221, 221, 221)',
            selectedTint: 'rgb(231, 248, 241)',
            selectedSurfaceEnd: 'rgb(255, 255, 255)',
            selectedBorder: 'rgb(4, 123, 67)',
            librarySelectedTint: 'rgb(241, 241, 241)',
            librarySelectedBorder: 'rgb(187, 187, 187)',
            editingTint: 'rgb(249, 242, 233)', // Figma #F9F2E9
            accent: 'rgb(4, 123, 67)', // Figma #047B43 primary (dark)
            mutedForeground: 'rgb(123, 123, 123)', // Figma #7B7B7B, the same grey as text-secondary
            hoverBackground: 'rgb(232, 232, 232)',
            hoverRule: 'rgb(187, 187, 187)',
            pendingForeground: 'rgb(150, 88, 10)',
        },
        cardStrip: {
            background: 'rgb(241, 241, 241)', // Figma #F1F1F1
            divider: 'rgb(221, 221, 221)', // Figma #DDDDDD — hairline between adjacent cards
            fade: 'rgb(255, 255, 255)',
            arrowBackground: 'rgb(255, 255, 255)',
            arrowBorder: 'rgb(232, 232, 232)', // Figma #E8E8E8, same hairline as dropdown.border
            addPrimaryBackground: 'rgb(10, 10, 10)',
            addPrimaryForeground: 'rgb(255, 255, 255)',
        },
        selectionBar: {
            background: 'rgb(10, 10, 10)', // Figma #0a0a0a
            foreground: 'rgb(255, 255, 255)',
            mutedForeground: 'rgb(123, 123, 123)',
            divider: 'rgb(64, 64, 64)',
            border: 'transparent', // edgeless in light
            primaryBorder: 'rgb(232, 232, 232)',
            shadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
            noticeBackground: 'rgb(237, 217, 190)',
            noticeForeground: 'rgb(132, 77, 9)',
            noticeShadow:
                '0px 0px 4px 0px rgba(0, 0, 0, 0.08), 0px 4px 4px 0px rgba(0, 0, 0, 0.08), 0px 4px 10px 0px rgba(0, 0, 0, 0.05)',
        },
        inlineConfirm: {
            background: 'rgb(34, 33, 33)', // Figma #222121
            foreground: 'rgb(255, 255, 255)',
            divider: 'rgba(255, 255, 255, 0.1)',
            shadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
        },
        dropdown: {
            border: 'rgb(232, 232, 232)',
            borderActive: 'rgba(0, 175, 102, 0.6)',
            borderFilter: 'rgba(0, 175, 102, 0.4)',
            borderError: 'rgba(239, 55, 55, 0.6)',
            borderPending: 'rgba(241, 139, 10, 0.6)',
            shadow: '0px 1px 0px 0px rgba(0, 0, 0, 0.15)',
            shadowHover: '0px 1px 0px 0px rgba(0, 175, 102, 0.6)',
            shadowActive: '0px 1px 0px 0px rgba(0, 175, 102, 0.8)',
            shadowFilter: '0px 1px 0px 0px rgba(0, 175, 102, 0.6)',
            shadowError:
                '0px 1px 0px 0px rgba(239, 55, 55, 0.8), 0px 0px 0px 2px rgba(239, 55, 55, 0.1)',
            shadowPending:
                '0px 1px 0px 0px rgba(241, 139, 10, 0.8), 0px 0px 0px 2px rgba(241, 139, 10, 0.1)',
            disabledBackground: 'rgb(241, 241, 241)', // Figma #f1f1f1
        },
        tag: {
            primary: {
                background: 'rgba(37, 130, 234, 0.1)', // #2582EA1A
                foreground: 'rgb(37, 130, 234)', // #2582EA
                border: 'rgb(37, 130, 234)', // #2582EA
                shadow: 'rgba(37, 130, 234, 0.5)', // #2582EA with 50% opacity
            },
            secondary: {
                background: 'rgba(0, 175, 102, 0.1)', // #00AF661A
                foreground: 'rgb(0, 175, 102)', // #00AF66
                border: 'rgb(0, 175, 102)', // #00AF66
                shadow: 'rgba(0, 175, 102, 0.5)', // #00AF66 with 50% opacity
            },
            default: {
                background: 'rgba(123, 123, 123, 0.1)', // #7B7B7B1A
                foreground: 'rgb(123, 123, 123)', // #7B7B7B
                border: 'rgb(123, 123, 123)', // #7B7B7B
                shadow: 'rgba(123, 123, 123, 0.5)', // #7B7B7B with 50% opacity
            },
            error: {
                background: 'rgba(196, 62, 62, 0.1)', // #C43E3E1A
                foreground: 'rgb(196, 62, 62)', // #C43E3E
                border: 'rgb(196, 62, 62)', // #C43E3E
                shadow: 'rgba(196, 62, 62, 0.5)', // #C43E3E with 50% opacity
            },
            warning: {
                background: 'rgba(150, 88, 10, 0.1)', // #96580A1A
                foreground: 'rgb(150, 88, 10)', // #96580A
                border: 'rgb(150, 88, 10)', // #96580A
                shadow: 'rgba(150, 88, 10, 0.5)', // #96580A with 50% opacity
            },
            success: {
                background: 'rgba(0, 175, 102, 0.1)', // #00AF661A
                foreground: 'rgb(2, 122, 66)', // #027A42
                border: 'rgb(2, 122, 66)', // #027A42
                shadow: 'rgba(2, 122, 66, 0.5)', // #027A42 with 50% opacity
            },
            emphasis: {
                background: 'rgba(123, 123, 123, 0.1)',
                foreground: 'rgb(10, 10, 10)',
                border: 'rgb(10, 10, 10)',
                shadow: 'rgba(0, 0, 0, 0.2)',
            },
            info: {
                background: 'rgba(12, 88, 174, 0.1)', // #0C58AE1A
                foreground: 'rgb(12, 88, 174)', // Blue (Dark) from Figma #0C58AE
                border: 'rgb(12, 88, 174)', // #0C58AE
                shadow: 'rgba(12, 88, 174, 0.5)', // #0C58AE with 50% opacity
            },
            assistant: {
                background: 'rgb(242, 238, 254)', // #F2EEFE
                foreground: 'rgb(110, 86, 207)', // #6E56CF
                border: 'rgb(227, 221, 251)', // #E3DDFB
                shadow: 'rgba(110, 86, 207, 0.5)', // #6E56CF with 50% opacity
            },
        },
        numberTag: {
            background: 'rgba(0, 0, 0, 0.08)',
            foreground: 'rgb(123, 123, 123)', // text secondary
            emphasisBackground: 'rgba(0, 0, 0, 0.15)',
            emphasisForeground: 'rgb(34, 33, 33)', // text emphasis
        },
        table: {
            background: 'rgb(247, 247, 247)', // #F7F7F7
            boxShadow: 'none', // The designed container is a flat grey frame, not elevated
            header: {
                background: 'rgb(247, 247, 247)', // #F7F7F7
                foreground: 'rgb(55, 65, 81)', // #374151
                border: 'rgb(229, 231, 235)', // #e5e7eb
            },
            row: {
                background: 'rgb(255, 255, 255)', // #ffffff
                foreground: 'rgb(75, 85, 99)', // #4b5563
                hoverBackground: 'rgb(249, 250, 251)', // #f9fafb
                border: 'rgb(229, 231, 235)', // #e5e7eb
                // Translucent, so it layers over the row (or a rowVariant tint). 15% not 10%:
                // at 10% the step was 1.11:1 from a plain row, near-invisible without colour
                // vision. Capped there to keep the foreground's AA headroom (4.64:1).
                selectedBackground: 'rgba(0, 175, 102, 0.15)', // #00AF66 at 15%
                selectedAccent: 'rgb(2, 122, 66)', // #027A42, matches text.success
                selectedForeground: 'rgb(2, 122, 66)', // #027A42
            },
            border: 'rgb(229, 231, 235)', // #e5e7eb
            borderedRow: {
                background: 'rgb(255, 255, 255)', // #FFF
                outline: 'rgb(232, 232, 232)', // #e8e8e8
            },
            rowVariant: {
                success: {
                    background: 'rgb(236, 249, 244)', // #ECF9F4
                },
                warning: {
                    background: 'rgb(253, 243, 230)', // #FDF3E6
                },
                error: {
                    background: 'rgb(253, 236, 236)', // #FDECEC
                },
                info: {
                    background: 'rgb(230, 244, 253)', // #E6F4FD
                },
            },
            error: {
                iconBackground: 'rgb(254, 243, 199)', // #fef3c7
                iconForeground: 'rgb(217, 119, 6)', // #d97706
                titleForeground: 'rgb(17, 24, 39)', // #111827
                subtitleForeground: 'rgb(107, 114, 128)', // #6b7280
            },
        },
        text: {
            primary: 'rgb(10, 10, 10)',
            secondary: 'rgb(123, 123, 123)',
            dimmed: 'rgb(161, 161, 161)',
            emphasis: 'rgb(34, 33, 33)',
            muted: 'rgb(76, 75, 75)',
            placeholder: 'rgb(187, 187, 187)', // Figma #bbbbbb for input placeholders
            destructive: 'rgb(237, 53, 54)',
            success: 'rgb(2, 122, 66)',
            highlight: 'rgb(118, 214, 174)',
            warning: 'rgb(150, 88, 10)',
            warningMuted: 'rgb(206, 163, 108)', // Figma #CEA36C
            error: 'rgb(237, 53, 54)',
            info: 'rgb(12, 88, 174)', // Blue (Dark) from Figma #0C58AE
            linkForeground: 'rgba(0, 0, 0, 0.45)',
            linkForegroundHover: 'rgb(10, 10, 10)',
            rightSideText: {
                muted: 'rgb(123, 123, 123)', // #7B7B7B for less prominent right side text
                primary: 'rgb(10, 10, 10)', // #0A0A0A for more prominent right side text
                alignment: 'right' as const, // Default to right alignment
                fontSize: '12px',
                minWidth: '80px',
            },
        },
        codeBlock: {
            background: 'rgb(255, 255, 255)', // White background from Figma
            foreground: 'rgb(10, 10, 10)', // Header color from Figma #0A0A0A
            border: 'rgb(232, 232, 232)', // Light border
            lineNumber: 'rgb(123, 123, 123)', // Text color from Figma #7B7B7B
            keyword: 'rgb(12, 88, 174)', // Blue (Dark) from Figma #0C58AE
            comment: 'rgb(123, 123, 123)', // Text color for comments
            string: 'rgb(2, 122, 66)', // Green for strings
            number: 'rgb(150, 88, 10)', // Orange for numbers
            type: 'rgb(12, 88, 174)', // Blue for types
            // Low alpha, so highlighted code stays readable through the tint.
            diffAddedBackground: 'rgba(0, 175, 102, 0.12)',
            diffRemovedBackground: 'rgba(239, 55, 55, 0.1)',
            // The assistant diff palette, from the design mock for AI-authored changes.
            diffAssistantBackground: '#F5F1FE',
            diffAssistantForeground: '#6E56CF',
            diffAssistantRemovedBackground: '#F7F6FB',
            diffRemovedForeground: '#5E5876',
            diffContextForeground: '#D9D9D9',
            diffContextText: '#3B3B3B',
        },
        chat: {
            userBubbleBackground: 'rgb(246, 246, 246)', // #F6F6F6
            userBubbleBorder: 'rgb(237, 237, 237)', // #EDEDED
            // Deliberately neutral, not violet. The violet marks a pending change in the form
            // beside this column; spending the same hue on "the assistant said it" leaves the
            // reader two meanings for one colour. The bubble differs from the user's by being
            // a white card against their filled grey, and the assistant is named in violet
            // above the thread instead.
            assistantBubbleBackground: 'rgb(255, 255, 255)',
            assistantBubbleBorder: 'rgb(232, 232, 232)', // The neutral hairline, as codeBlock.border
            assistantBubbleForeground: 'rgb(10, 10, 10)', // text.primary — this is body copy, read at length
        },
        assistant: {
            foreground: 'rgb(110, 86, 207)', // #6E56CF, matches tag.assistant.foreground
            surfaceBackground: 'rgb(251, 250, 255)', // #FBFAFF, a faint tint for a card or callout
            surfaceBorder: 'rgb(220, 211, 250)', // #DCD3FA, drawn dashed by the consumer
            surfaceForeground: 'rgb(74, 63, 122)', // #4A3F7A, from the Chat.Suggestion mock
        },
    },
};

// Dark theme colors
export const darkTheme: MalachiteTheme = {
    fontSizes: {
        xsmall: '11px',
        small: '13px',
        medium: '14px',
        '15': '15px',
        large: '16px',
        xlarge: '18px',
        xxlarge: '22px',
    },
    controlHeights: { ...CONTROL_HEIGHT },
    iconSizes: {
        xsmall: '12px',
        small: '13px',
        medium: '13px',
        large: '15px',
    },
    radius: {
        xs: '2px',
        sm: '4px',
        md: '6px',
        lg: '8px',
        '10': '10px',
        xl: '12px',
        xxl: '14px',
        xxxl: '16px',
    },
    shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.3)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -4px rgba(0, 0, 0, 0.3)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)',
        glow: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)',
        glowLg: '0px 0px 20px 0px rgba(0, 0, 0, 0.5)',
        default: '0px 1px 0px 0px rgba(0, 0, 0, 0.4)',
        accent: '0px 1px 0px 0px rgba(0, 175, 102, 0.4)',
        success: '0px 1px 0px 0px rgba(0, 175, 102, 0.4)',
        warning: '0px 1px 0px 0px rgba(241, 139, 10, 0.4)',
        error: '0px 1px 0px 0px rgba(197, 63, 63, 0.4)',
    },
    colors: {
        shadowColor: '0, 0, 0',
        shadowOpacity: 0.3,
        card: {
            background: 'rgb(30, 30, 30)',
            foreground: 'rgb(255, 255, 255)',
            border: 'rgb(64, 64, 64)',
            shadow: 'rgba(0, 0, 0, 0.3)',
            dimmedBackground: 'rgb(70, 70, 70)',
            sunkenBackground: 'rgb(10, 10, 10)',
            warmBackground: 'rgb(45, 43, 41)',
            headerBackground: 'rgb(30, 30, 30)',
            footerBackground: 'rgb(30, 30, 30)',
            footerGlassBackground: 'rgba(30, 30, 30, 0.7)',
            trayNeutral: 'rgb(56, 56, 56)',
            trayWarning: 'rgb(74, 62, 29)',
            trayError: 'rgb(70, 44, 44)',
            traySuccess: 'rgb(31, 63, 40)',
            subtleDivider: 'rgb(48, 48, 48)',
        },
        overlay: {
            scrim: 'rgba(0, 0, 0, 0.4)',
        },
        alert: {
            info: {
                background: 'rgb(70, 70, 70)',
                foreground: 'rgb(255, 255, 255)',
            },
            success: {
                background: 'rgba(0, 175, 102, 0.08)',
                foreground: 'rgb(2, 122, 66)',
            },
            error: {
                background: 'rgba(197, 63, 63, 0.08)',
                foreground: 'rgb(237, 53, 54)',
            },
            warning: {
                background: 'rgba(241, 139, 10, 0.08)',
                foreground: 'rgb(150, 88, 10)',
            },
            assistant: {
                // The dark violet the assistant Tag and diff palette already use; the light
                // pair here would render an opaque near-white block on a dark surface.
                background: 'rgba(158, 130, 255, 0.18)',
                foreground: 'rgb(184, 158, 255)',
            },
        },
        tooltip: {
            background: 'rgb(42, 42, 42)',
            foreground: 'rgb(255, 255, 255)',
            border: 'rgb(80, 80, 80)',
            shadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.30)',
        },
        primary: {
            background: 'rgb(60, 60, 60)',
            foreground: 'rgb(255, 255, 255)',
            border: 'rgb(100, 100, 100)',
            borderHover: 'rgb(140, 140, 140)',
        },
        accent: {
            background: 'rgba(50, 205, 132)',
            foreground: 'rgb(20, 170, 100)',
            border: 'rgba(50, 205, 132, 0.3)',
            backgroundGlass: 'rgba(50, 205, 132, 0.12)',
        },
        control: {
            borderHover: 'rgba(50, 205, 132, 0.5)',
        },
        outline: {
            foreground: 'rgb(255, 255, 255)',
        },
        // Filled buttons invert in dark mode: white surface, near-black text.
        secondary: {
            background: 'rgb(255, 255, 255)',
            foreground: 'rgb(10, 10, 10)',
            border: 'rgb(255, 255, 255)',
            borderHover: 'rgb(220, 220, 220)',
        },
        destructive: {
            background: 'transparent',
            foreground: 'rgb(255, 120, 120)',
            border: 'rgb(255, 100, 100)',
            borderHover: 'rgb(255, 100, 100)',
            shadow: 'rgb(255, 100, 100)',
            shadowLight: 'rgba(255, 100, 100, 0.4)',
            shadowMedium: 'rgba(255, 100, 100, 0.6)',
        },
        success: {
            background: 'rgba(50, 205, 132)',
            foreground: 'rgb(20, 170, 100)',
            border: 'rgba(50, 205, 132, 0.3)',
            borderHover: 'rgba(50, 205, 132, 0.5)',
            backgroundGlass: 'rgba(50, 205, 132, 0.12)',
            shadowLight: 'rgba(50, 205, 132, 0.4)',
            shadowMedium: 'rgba(50, 205, 132, 0.6)',
        },
        warning: {
            background: 'rgb(255, 180, 50)',
            foreground: 'rgb(200, 130, 20)',
            border: 'rgba(255, 180, 50, 0.3)',
            borderHover: 'rgba(255, 180, 50, 0.5)',
            backgroundGlass: 'rgba(255, 180, 50, 0.12)',
            shadowLight: 'rgba(255, 180, 50, 0.4)',
            shadowMedium: 'rgba(255, 180, 50, 0.6)',
        },
        error: {
            background: 'rgb(255, 120, 120)',
            foreground: 'rgb(255, 80, 80)',
            border: 'rgba(255, 120, 120, 0.3)',
            borderHover: 'rgba(255, 120, 120, 0.5)',
            backgroundGlass: 'rgba(255, 120, 120, 0.12)',
            shadowLight: 'rgba(255, 120, 120, 0.4)',
            shadowMedium: 'rgba(255, 120, 120, 0.6)',
        },
        buttonList: {
            background: 'rgb(30, 30, 30)',
            foreground: 'rgb(255, 255, 255)',
            hoverBackground: 'rgb(60, 60, 60)',
            hoverForeground: 'rgb(255, 255, 255)',
        },
        pillButton: {
            background: 'rgb(70, 70, 70)', // Dark theme equivalent
            foreground: 'rgb(161, 161, 161)', // Dark theme equivalent
            hoverBackground: 'rgb(90, 90, 90)', // Lighter for dark theme hover
            hoverForeground: 'rgb(255, 255, 255)', // White text on hover in dark mode
            pressedBackground: 'rgb(90, 90, 90)', // Same as hover for dark theme
            pressedForeground: 'rgb(255, 255, 255)', // Same as hover for dark theme
            pressedOpacity: '0.6', // Same opacity reduction for dark theme
            activeBackground: 'rgb(255, 255, 255)',
            activeForeground: 'rgb(10, 10, 10)',
            staticBackground: 'rgb(60, 60, 60)',
            staticForeground: 'rgb(220, 220, 220)',
            invertedBackground: 'rgb(38, 38, 38)',
            invertedForeground: 'rgb(220, 220, 220)',
            invertedHoverBackground: 'rgb(255, 255, 255)',
            invertedHoverForeground: 'rgb(10, 10, 10)',
            invertedPressedBackground: 'rgb(255, 255, 255)',
            invertedPressedForeground: 'rgb(10, 10, 10)',
        },
        toggle: {
            track: 'rgb(70, 70, 70)', // neutral off track for dark theme
            trackChecked: 'rgb(34, 197, 94)', // lighter green to match dark theme success
            trackCheckedPending: 'rgb(240, 138, 7)', // dark-theme pending amber
            knob: 'rgb(255, 255, 255)',
        },
        segmentedControl: {
            // Inverted from light mode: the track is the DARKER surface and the selected
            // thumb sits lighter on top, so "raised" still reads as raised.
            trackBackground: 'rgb(38, 38, 38)',
            itemForeground: 'rgb(161, 161, 161)', // text.secondary — 5.9:1 on the track
            itemHoverForeground: 'rgb(255, 255, 255)',
            selectedBackground: 'rgb(82, 82, 82)',
            selectedForeground: 'rgb(255, 255, 255)', // 7.8:1 on the thumb
            selectedShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.4)',
        },
        selectableCard: {
            background: 'rgb(45, 45, 45)',
            border: 'rgb(64, 64, 64)',
            selectedTint: 'rgb(30, 62, 48)',
            selectedSurfaceEnd: 'rgb(52, 52, 52)',
            selectedBorder: 'rgb(50, 205, 132)',
            librarySelectedTint: 'rgb(45, 45, 45)',
            librarySelectedBorder: 'rgb(115, 115, 115)',
            editingTint: 'rgb(58, 48, 36)', // the amber tint re-picked for a dark sheet
            accent: 'rgb(50, 205, 132)', // dark success — #047B43 is unreadable on dark
            mutedForeground: 'rgb(161, 161, 161)', // 4.82:1 on the selected card, 5.86:1 on the trough
            hoverBackground: 'rgb(64, 64, 64)',
            hoverRule: 'rgb(115, 115, 115)',
            pendingForeground: 'rgb(240, 138, 7)',
        },
        cardStrip: {
            background: 'rgb(38, 38, 38)', // one step under the selected card, as #F1F1F1 is under white
            divider: 'rgb(64, 64, 64)',
            fade: 'rgb(30, 30, 30)', // matches card.background so the mask disappears into it
            arrowBackground: 'rgb(45, 45, 45)',
            arrowBorder: 'rgb(100, 100, 100)', // dark dropdown.border, the light pair of #E8E8E8
            addPrimaryBackground: 'rgb(245, 245, 245)',
            addPrimaryForeground: 'rgb(23, 23, 23)',
        },
        selectionBar: {
            background: 'rgb(56, 56, 56)', // lifts off the dark sheet
            foreground: 'rgb(255, 255, 255)',
            mutedForeground: 'rgb(176, 176, 176)',
            divider: 'rgb(96, 96, 96)',
            border: 'rgb(96, 96, 96)', // hairline on the dark sheet
            primaryBorder: 'rgb(120, 120, 120)',
            shadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.45)',
            noticeBackground: 'rgb(96, 70, 28)',
            noticeForeground: 'rgb(240, 202, 148)',
            noticeShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.45)',
        },
        inlineConfirm: {
            background: 'rgb(64, 64, 64)', // lifts off the dark sheet
            foreground: 'rgb(255, 255, 255)',
            divider: 'rgba(255, 255, 255, 0.16)',
            shadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.45)',
        },
        dropdown: {
            border: 'rgb(100, 100, 100)',
            borderActive: 'rgba(50, 205, 132, 0.7)',
            borderFilter: 'rgba(50, 205, 132, 0.5)',
            borderError: 'rgba(255, 100, 100, 0.6)',
            borderPending: 'rgba(240, 138, 7, 0.7)',
            shadow: '0px 1px 0px 0px rgba(0, 0, 0, 0.4)',
            shadowHover: '0px 1px 0px 0px rgba(50, 205, 132, 0.6)',
            shadowActive: '0px 1px 0px 0px rgba(50, 205, 132, 0.8)',
            shadowFilter: '0px 1px 0px 0px rgba(50, 205, 132, 0.6)',
            shadowError:
                '0px 1px 0px 0px rgba(255, 100, 100, 0.8), 0px 0px 0px 2px rgba(255, 100, 100, 0.1)',
            shadowPending:
                '0px 1px 0px 0px rgba(240, 138, 7, 0.8), 0px 0px 0px 2px rgba(240, 138, 7, 0.15)',
            disabledBackground: 'rgb(70, 70, 70)',
        },
        tag: {
            primary: {
                background: 'rgba(37, 130, 234, 0.2)', // Darker background for dark theme
                foreground: 'rgb(96, 165, 250)', // Lighter blue for dark theme
                border: 'rgb(96, 165, 250)', // Lighter blue border
                shadow: 'rgba(96, 165, 250, 0.5)', // Lighter blue shadow
            },
            secondary: {
                background: 'rgba(0, 175, 102, 0.2)', // Darker background for dark theme
                foreground: 'rgb(34, 197, 94)', // Lighter green for dark theme
                border: 'rgb(34, 197, 94)', // Lighter green border
                shadow: 'rgba(34, 197, 94, 0.5)', // Lighter green shadow
            },
            default: {
                background: 'rgba(161, 161, 161, 0.2)', // Darker background for dark theme
                foreground: 'rgb(161, 161, 161)', // Light gray for dark theme
                border: 'rgb(161, 161, 161)', // Light gray border
                shadow: 'rgba(161, 161, 161, 0.5)', // Light gray shadow
            },
            error: {
                background: 'rgba(239, 68, 68, 0.2)', // Darker background for dark theme
                foreground: 'rgb(248, 113, 113)', // Lighter red for dark theme
                border: 'rgb(248, 113, 113)', // Lighter red border
                shadow: 'rgba(248, 113, 113, 0.5)', // Lighter red shadow
            },
            warning: {
                background: 'rgba(245, 158, 11, 0.2)', // Darker background for dark theme
                foreground: 'rgb(251, 191, 36)', // Lighter orange for dark theme
                border: 'rgb(251, 191, 36)', // Lighter orange border
                shadow: 'rgba(251, 191, 36, 0.5)', // Lighter orange shadow
            },
            success: {
                background: 'rgba(0, 175, 102, 0.2)', // Darker background for dark theme
                foreground: 'rgb(34, 197, 94)', // Lighter green for dark theme
                border: 'rgb(34, 197, 94)', // Lighter green border
                shadow: 'rgba(34, 197, 94, 0.5)', // Lighter green shadow
            },
            emphasis: {
                background: 'rgb(59, 59, 59)', // Figma #3b3b3b
                foreground: 'rgb(255, 255, 255)',
                border: 'rgb(255, 255, 255)',
                shadow: 'rgba(0, 0, 0, 0.2)',
            },
            info: {
                background: 'rgba(12, 88, 174, 0.25)',
                foreground: 'rgb(125, 171, 245)', // Blue (Dark) lifted for dark surfaces
                border: 'rgb(125, 171, 245)',
                shadow: 'rgba(125, 171, 245, 0.5)',
            },
            assistant: {
                // Same violet the assistant diff palette already picked for dark ground
                // (see codeBlock.diffAssistantBackground below) — one hue, reused everywhere
                // "made by the assistant" needs to read in dark mode.
                background: 'rgba(158, 130, 255, 0.18)',
                foreground: 'rgb(184, 158, 255)', // Matches diffAssistantForeground
                border: 'rgba(158, 130, 255, 0.32)', // Same hue as the background, more opaque so the edge reads
                shadow: 'rgba(184, 158, 255, 0.5)', // Lighter violet shadow
            },
        },
        numberTag: {
            background: 'rgba(255, 255, 255, 0.1)', // Light pill on dark surfaces
            foreground: 'rgb(161, 161, 161)', // Dark theme text secondary
            emphasisBackground: 'rgba(255, 255, 255, 0.2)', // Stronger pill for emphasis
            emphasisForeground: 'rgb(237, 237, 237)', // Dark theme text emphasis
        },
        table: {
            // Copied from the light theme and never darkened, so dark mode rendered a light
            // container, header and white rows with pale grey text.
            background: 'rgb(23, 23, 23)', // Darker than rows, as #F7F7F7 is in light
            boxShadow: 'none', // The designed container is a flat grey frame, not elevated
            header: {
                background: 'rgb(42, 42, 42)', // Dark theme header surface
                foreground: 'rgb(237, 237, 237)', // Dark theme text emphasis
                border: 'rgb(75, 85, 99)', // Dark theme header border
            },
            row: {
                background: 'rgb(30, 30, 30)', // Dark theme row background
                foreground: 'rgb(209, 213, 219)', // Dark theme row text
                hoverBackground: 'rgb(42, 42, 42)', // Dark theme row hover
                border: 'rgb(75, 85, 99)', // Dark theme row border
                // Not the light theme's brand green: over a near-black row it composites darker
                // than hoverBackground. 20%, because at 15% the step past hover was 1.12:1.
                selectedBackground: 'rgba(34, 197, 94, 0.2)',
                selectedAccent: 'rgb(20, 170, 100)', // non-text, 3:1 applies
                selectedForeground: 'rgb(34, 197, 94)', // 5.09:1 — text needs AA 4.5:1
            },
            border: 'rgb(75, 85, 99)', // Dark theme table border
            borderedRow: {
                background: 'rgb(30, 30, 30)', // Matches row.background and card.background
                outline: 'rgb(75, 85, 99)', // Dark theme bordered row outline
            },
            rowVariant: {
                success: {
                    background: 'rgba(34, 197, 94, 0.15)', // Dark theme success background
                },
                warning: {
                    background: 'rgba(251, 191, 36, 0.15)', // Dark theme warning background
                },
                error: {
                    background: 'rgba(248, 113, 113, 0.15)', // Dark theme error background
                },
                info: {
                    background: 'rgba(96, 165, 250, 0.15)', // Dark theme info background
                },
            },
            error: {
                iconBackground: 'rgba(245, 158, 11, 0.2)', // Dark theme warning background
                iconForeground: 'rgb(251, 191, 36)', // Dark theme warning foreground
                titleForeground: 'rgb(255, 255, 255)', // Dark theme title
                subtitleForeground: 'rgb(161, 161, 161)', // Dark theme subtitle
            },
        },
        text: {
            primary: 'rgb(255, 255, 255)',
            secondary: 'rgb(161, 161, 161)',
            dimmed: 'rgb(115, 115, 115)',
            emphasis: 'rgb(237, 237, 237)',
            muted: 'rgb(199, 199, 199)',
            placeholder: 'rgb(140, 140, 140)', // Dark theme placeholder color
            destructive: 'rgb(255, 120, 120)',
            success: 'rgb(20, 170, 100)',
            highlight: 'rgb(118, 214, 174)',
            warning: 'rgb(200, 130, 20)',
            warningMuted: 'rgb(206, 163, 108)', // Same tan reads as muted on dark too
            error: 'rgb(255, 80, 80)',
            info: 'rgb(125, 171, 245)',
            linkForeground: 'rgba(255, 255, 255, 0.45)',
            linkForegroundHover: 'rgb(255, 255, 255)',
            rightSideText: {
                // Mirrors text.secondary / text.primary, exactly as the light theme does.
                muted: 'rgb(161, 161, 161)',
                primary: 'rgb(255, 255, 255)',
                alignment: 'right' as const,
                fontSize: '12px',
                minWidth: '80px',
            },
        },
        codeBlock: {
            background: 'rgb(42, 42, 42)', // Dark background
            foreground: 'rgb(255, 255, 255)', // Light text
            border: 'rgb(80, 80, 80)', // Dark border
            lineNumber: 'rgb(161, 161, 161)', // Light gray for line numbers
            keyword: 'rgb(100, 150, 220)', // Light blue for keywords
            comment: 'rgb(161, 161, 161)', // Gray for comments
            string: 'rgb(120, 220, 150)', // Light green for strings
            number: 'rgb(255, 180, 100)', // Light orange for numbers
            type: 'rgb(100, 150, 220)', // Light blue for types
            diffAddedBackground: 'rgba(34, 197, 94, 0.16)', // Lighter green for dark theme
            diffRemovedBackground: 'rgba(255, 100, 100, 0.14)', // Lighter red for dark theme
            // Assistant diff palette, kept at the same relationships as the light theme values.
            diffAssistantBackground: 'rgba(158, 130, 255, 0.18)', // Violet tint that still reads on a dark ground
            diffAssistantForeground: 'rgb(184, 158, 255)', // Lighter violet so the marker holds contrast
            diffAssistantRemovedBackground: 'rgba(190, 185, 210, 0.08)', // Faint neutral-violet tint
            diffRemovedForeground: 'rgb(168, 162, 190)', // Muted violet-grey marker, lightened for dark contrast
            diffContextForeground: 'rgb(90, 90, 90)', // Low-contrast marker, dark counterpart to the near-white D9D9D9
            diffContextText: 'rgb(170, 170, 170)', // De-emphasised text, in line with the existing comment grey
        },
        chat: {
            userBubbleBackground: 'rgb(45, 45, 45)', // One step up from the page, matching selectableCard's dark surface
            userBubbleBorder: 'rgb(64, 64, 64)', // Matches card.border, the dark theme's neutral hairline
            // Neutral for the same reason as the light theme: the violet belongs to the
            // pending-change marks, not to authorship. Here the assistant sits one step BELOW
            // the user's surface rather than above it, since dark has no room above 45.
            assistantBubbleBackground: 'rgb(30, 30, 30)', // card.background
            assistantBubbleBorder: 'rgb(64, 64, 64)', // card.border, the same hairline the user bubble uses
            assistantBubbleForeground: 'rgb(255, 255, 255)', // card.foreground
        },
        assistant: {
            foreground: 'rgb(184, 158, 255)', // Matches diffAssistantForeground / tag.assistant.foreground
            surfaceBackground: 'rgba(158, 130, 255, 0.08)', // Faint tint, still distinguishable from the dark page
            surfaceBorder: 'rgba(158, 130, 255, 0.35)', // A dashed outline needs more presence than the surface tint
            // Light theme darkens the violet accent (#6E56CF) to #4A3F7A for body text; dark
            // theme instead lifts it, the same transform `text.info` applies to its accent, so
            // the muted violet still reads against a near-black ground rather than going darker.
            surfaceForeground: 'rgb(134, 120, 186)',
        },
    },
};

// localStorage keys
const THEME_MODE_KEY = 'malachite-theme-mode';
const CUSTOM_THEME_KEY = 'malachite-custom-theme';

// hub forwards a caller-supplied theme from a JSON attribute into applyTheme, and the
// shadow-root branch concatenates values into a CSS string — any of these closes :host.
/** A custom property name is only safe to echo into a log once it matches this. */
const SAFE_CUSTOM_PROPERTY = /^--[A-Za-z0-9_-]+$/;

const UNSAFE_CSS_SYNTAX = /[{};]|\/\*|\*\//;
// Keys become custom-property names, so they carry the same risk.
const SAFE_THEME_KEY = /^[A-Za-z0-9_-]+$/;

/** Drops any key or value that could break out of a CSS declaration. Dropped entries fall
 *  through to the base theme rather than leaving the token unset. */
function sanitizeThemeInput(value: unknown, path: string): unknown {
    if (typeof value === 'string') {
        if (UNSAFE_CSS_SYNTAX.test(value)) {
            console.warn(
                `Malachite: ignoring theme value at ${path} — CSS syntax characters ({ } ; /* */) are not allowed.`,
            );
            return undefined;
        }
        return value;
    }

    if (value === null || typeof value !== 'object') return value;

    const sanitized: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        const childPath = path ? `${path}.${key}` : key;
        if (!SAFE_THEME_KEY.test(key)) {
            // Parent path, never childPath: the rejected key is untrusted input.
            console.warn(
                `Malachite: ignoring a theme key under ${path || '<root>'} — theme keys may contain only letters, digits, hyphens and underscores.`,
            );
            continue;
        }
        const sanitizedChild = sanitizeThemeInput(child, childPath);
        if (sanitizedChild !== undefined) sanitized[key] = sanitizedChild;
    }
    return sanitized;
}

// Keep track of all shadow roots so we can update them when theme changes
const registeredShadowRoots = new Set<ShadowRoot>();

/**
 * Register a shadow root to receive theme updates
 */
export function registerShadowRoot(shadowRoot: ShadowRoot): void {
    try {
        // Prevent duplicate registrations
        if (registeredShadowRoots.has(shadowRoot)) {
            return;
        }

        registeredShadowRoots.add(shadowRoot);
        // Apply current theme to this new shadow root
        applyCurrentThemeToShadowRoot(shadowRoot);
    } catch (error) {
        console.error('Error registering shadow root:', error);
    }
}

/**
 * Unregister a shadow root when it's no longer needed
 */
export function unregisterShadowRoot(shadowRoot: ShadowRoot): void {
    try {
        registeredShadowRoots.delete(shadowRoot);
    } catch (error) {
        console.error('Error unregistering shadow root:', error);
    }
}

/** Table keys whose readers expect kebab-case. `boxShadow` was emitted camelCase while
 *  tailwind.css and getCurrentTheme both read `--malachite-table-box-shadow`, so the
 *  bordered container never received a shadow. Keyed on the real table shape, so a typo
 *  fails typecheck rather than silently emitting the camelCase name. */
const TABLE_KEBAB_KEYS: Partial<Record<keyof MalachiteTheme['colors']['table'], string>> = {
    rowVariant: 'row-variant',
    boxShadow: 'box-shadow',
};

/**
 * Helper function to process table color properties and add them to CSS properties
 */
function processTableColorProperties(
    tableColors: MalachiteTheme['colors']['table'],
    prefix: string,
    addProperty: (key: string, value: string) => void,
): void {
    Object.entries(tableColors).forEach(([key, value]) => {
        // Top-level table keys are NOT kebab-cased in general: `borderedRow` ships as
        // `--malachite-table-borderedRow-*` and tailwind.css reads it that way, so
        // renaming it would break consumers. Only the keys whose readers expect kebab
        // are mapped, hence the explicit list rather than a blanket transform.
        const parentKeyVar = TABLE_KEBAB_KEYS[key as keyof typeof TABLE_KEBAB_KEYS] ?? key;
        if (typeof value === 'object' && value !== null) {
            Object.entries(value).forEach(([subKey, subValue]) => {
                const kebabKey = subKey.replace(/([A-Z])/g, '-$1').toLowerCase();
                if (typeof subValue === 'object' && subValue !== null) {
                    Object.entries(subValue).forEach(([deepKey, deepValue]) => {
                        const deepKebabKey = deepKey.replace(/([A-Z])/g, '-$1').toLowerCase();
                        addProperty(
                            `${prefix}-${parentKeyVar}-${kebabKey}-${deepKebabKey}`,
                            deepValue as string,
                        );
                    });
                } else {
                    addProperty(`${prefix}-${parentKeyVar}-${kebabKey}`, subValue as string);
                }
            });
        } else {
            addProperty(`${prefix}-${parentKeyVar}`, value);
        }
    });
}

/**
 * Apply CSS custom properties to a target element (document.documentElement or shadow root)
 */
function applyCSSCustomProperties(theme: MalachiteTheme, target: HTMLElement | ShadowRoot): void {
    // Both branches emit accent from these, so an omitted accent still paints the defaults.
    const accentColors = theme.colors.accent ?? DEFAULT_ACCENT_COLORS;
    const shadows = { ...theme.shadows, accent: theme.shadows.accent ?? DEFAULT_ACCENT_SHADOW };

    if (target instanceof ShadowRoot) {
        // For shadow roots, we need to set properties on a style element
        let styleElement = target.querySelector('#malachite-theme-vars') as HTMLStyleElement;
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'malachite-theme-vars';
            target.appendChild(styleElement);
        }

        // Build CSS custom properties
        const properties: string[] = [];

        // Add font size properties
        Object.entries(theme.fontSizes).forEach(([key, value]) => {
            properties.push(`--malachite-font-size-${key}: ${value}`);
        });

        // Add icon size properties
        Object.entries(theme.iconSizes).forEach(([key, value]) => {
            properties.push(`--malachite-icon-size-${key}: ${value}`);
        });

        // Add radius properties
        Object.entries(theme.radius).forEach(([key, value]) => {
            properties.push(`--malachite-radius-${key}: ${value}`);
        });

        // Add control height properties
        Object.entries(theme.controlHeights ?? CONTROL_HEIGHT).forEach(([key, value]) => {
            properties.push(`--malachite-control-height-${key}: ${value}`);
        });

        // Add shadow properties
        Object.entries(shadows).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-shadow-${kebabKey}: ${value}`);
        });

        // Add color properties
        properties.push(`--malachite-shadow-color: ${theme.colors.shadowColor}`);
        properties.push(`--malachite-shadow-opacity: ${theme.colors.shadowOpacity}`);

        // Add card color properties
        Object.entries(theme.colors.card).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-card-${kebabKey}: ${value}`);
        });

        // Add overlay color properties
        Object.entries(theme.colors.overlay).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-overlay-${kebabKey}: ${value}`);
        });

        // Add alert color properties
        Object.entries(theme.colors.alert).forEach(([alertType, alertColors]) => {
            Object.entries(alertColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                properties.push(`--malachite-alert-${alertType}-${kebabKey}: ${value}`);
            });
        });

        // Add tooltip color properties
        Object.entries(theme.colors.tooltip).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-tooltip-${kebabKey}: ${value}`);
        });

        // Add buttonList color properties
        Object.entries(theme.colors.buttonList).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-buttonlist-${kebabKey}: ${value}`);
        });

        // Add pillButton color properties
        Object.entries(theme.colors.pillButton).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-pillbutton-${kebabKey}: ${value}`);
        });

        // Add toggle color properties
        Object.entries(theme.colors.toggle).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-toggle-${kebabKey}: ${value}`);
        });

        // Add segmentedControl color properties
        Object.entries(theme.colors.segmentedControl).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-segmented-control-${kebabKey}: ${value}`);
        });

        Object.entries(theme.colors.selectableCard).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-selectable-card-${kebabKey}: ${value}`);
        });

        Object.entries(theme.colors.cardStrip).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-card-strip-${kebabKey}: ${value}`);
        });

        Object.entries(theme.colors.selectionBar).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-selection-bar-${kebabKey}: ${value}`);
        });

        Object.entries(theme.colors.inlineConfirm).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-inline-confirm-${kebabKey}: ${value}`);
        });

        // Add dropdown color properties
        Object.entries(theme.colors.dropdown).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-dropdown-${kebabKey}: ${value}`);
        });

        Object.entries(theme.colors.control).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-control-${kebabKey}: ${value}`);
        });

        Object.entries(accentColors).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-accent-${kebabKey}: ${value}`);
        });

        // Add tag color properties
        Object.entries(theme.colors.tag).forEach(([tagType, tagColors]) => {
            Object.entries(tagColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                properties.push(`--malachite-tag-${tagType}-${kebabKey}: ${value}`);
            });
        });

        // Add number tag color properties
        Object.entries(theme.colors.numberTag).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-number-tag-${kebabKey}: ${value}`);
        });

        // Add table color properties
        processTableColorProperties(theme.colors.table, '--malachite-table', (key, value) =>
            properties.push(`${key}: ${value}`),
        );

        // Add text color properties
        Object.entries(theme.colors.text).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            if (typeof value === 'object' && value !== null) {
                // Handle nested objects like rightSideText (supports one extra nesting level)
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                    const nestedKebabKey = nestedKey.replace(/([A-Z])/g, '-$1').toLowerCase();
                    if (typeof nestedValue === 'object' && nestedValue !== null) {
                        Object.entries(nestedValue as Record<string, string>).forEach(
                            ([deepKey, deepValue]) => {
                                const deepKebabKey = deepKey
                                    .replace(/([A-Z])/g, '-$1')
                                    .toLowerCase();
                                properties.push(
                                    `--malachite-text-${kebabKey}-${nestedKebabKey}-${deepKebabKey}: ${deepValue}`,
                                );
                            },
                        );
                    } else {
                        properties.push(
                            `--malachite-text-${kebabKey}-${nestedKebabKey}: ${nestedValue}`,
                        );
                    }
                });
            } else {
                properties.push(`--malachite-text-${kebabKey}: ${value}`);
            }
        });

        // Add codeBlock color properties
        Object.entries(theme.colors.codeBlock).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-codeblock-${kebabKey}: ${value}`);
        });

        // Add chat color properties
        Object.entries(theme.colors.chat).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-chat-${kebabKey}: ${value}`);
        });

        // Add assistant color properties
        Object.entries(theme.colors.assistant).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            properties.push(`--malachite-assistant-${kebabKey}: ${value}`);
        });

        // Add button color variants
        // Keep in sync with the documentElement branch's list: 'outline' was missing here,
        // so the token never reached a shadow root — where every component actually renders.
        const colorVariants = [
            'primary',
            'secondary',
            'outline',
            'destructive',
            'success',
            'warning',
            'error',
        ] as const;
        colorVariants.forEach((variant) => {
            const variantColors = theme.colors[variant];
            Object.entries(variantColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                properties.push(`--malachite-${variant}-${kebabKey}: ${value}`);
            });
        });

        // :host only — custom properties inherit to the whole shadow tree, and a
        // universal `* { }` copy forces WebKit to apply/compare every token on every
        // element, which makes style recalc O(elements × tokens) and freezes Safari
        // on large pages. Component-level token overrides still win over inheritance.
        // Second line of defence behind mergeThemes' sanitizer.
        const safeProperties = properties.filter((declaration) => {
            if (!UNSAFE_CSS_SYNTAX.test(declaration)) return true;
            // A hostile key puts the breakout before the first colon, so check the name too.
            const candidate = declaration.split(':', 1)[0].trim();
            const name = SAFE_CUSTOM_PROPERTY.test(candidate) ? candidate : 'an unnamed token';
            console.warn(`Malachite: dropped an unsafe theme declaration for ${name}`);
            return false;
        });

        styleElement.textContent = `:host { ${safeProperties.join('; ')} }`;
    } else if (target instanceof HTMLElement) {
        // For document.documentElement, use setProperty directly
        Object.entries(theme.fontSizes).forEach(([key, value]) => {
            target.style.setProperty(`--malachite-font-size-${key}`, value);
        });

        Object.entries(theme.iconSizes).forEach(([key, value]) => {
            target.style.setProperty(`--malachite-icon-size-${key}`, value);
        });

        Object.entries(theme.radius).forEach(([key, value]) => {
            target.style.setProperty(`--malachite-radius-${key}`, value);
        });

        Object.entries(theme.controlHeights ?? CONTROL_HEIGHT).forEach(([key, value]) => {
            target.style.setProperty(`--malachite-control-height-${key}`, value);
        });

        Object.entries(shadows).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-shadow-${kebabKey}`, value);
        });

        target.style.setProperty('--malachite-shadow-color', theme.colors.shadowColor);
        target.style.setProperty(
            '--malachite-shadow-opacity',
            theme.colors.shadowOpacity.toString(),
        );

        // Add card color properties
        Object.entries(theme.colors.card).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-card-${kebabKey}`, value);
        });

        // Add overlay color properties
        Object.entries(theme.colors.overlay).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-overlay-${kebabKey}`, value);
        });

        // Add alert color properties
        Object.entries(theme.colors.alert).forEach(([alertType, alertColors]) => {
            Object.entries(alertColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                target.style.setProperty(`--malachite-alert-${alertType}-${kebabKey}`, value);
            });
        });

        // Add tooltip color properties
        Object.entries(theme.colors.tooltip).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-tooltip-${kebabKey}`, value);
        });

        // Add buttonList color properties
        Object.entries(theme.colors.buttonList).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-buttonlist-${kebabKey}`, value);
        });

        // Add pillButton color properties
        Object.entries(theme.colors.pillButton).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-pillbutton-${kebabKey}`, value);
        });

        // Add toggle color properties
        Object.entries(theme.colors.toggle).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-toggle-${kebabKey}`, value);
        });

        // Add segmentedControl color properties
        Object.entries(theme.colors.segmentedControl).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-segmented-control-${kebabKey}`, value);
        });

        Object.entries(theme.colors.selectableCard).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-selectable-card-${kebabKey}`, value);
        });

        Object.entries(theme.colors.cardStrip).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-card-strip-${kebabKey}`, value);
        });

        Object.entries(theme.colors.selectionBar).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-selection-bar-${kebabKey}`, value);
        });

        Object.entries(theme.colors.inlineConfirm).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-inline-confirm-${kebabKey}`, value);
        });

        // Add dropdown color properties
        Object.entries(theme.colors.dropdown).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-dropdown-${kebabKey}`, value);
        });

        Object.entries(theme.colors.control).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-control-${kebabKey}`, value);
        });

        Object.entries(accentColors).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-accent-${kebabKey}`, value);
        });

        // Add tag color properties
        Object.entries(theme.colors.tag).forEach(([tagType, tagColors]) => {
            Object.entries(tagColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                target.style.setProperty(`--malachite-tag-${tagType}-${kebabKey}`, value);
            });
        });

        // Add number tag color properties
        Object.entries(theme.colors.numberTag).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-number-tag-${kebabKey}`, value);
        });

        // Add table color properties
        processTableColorProperties(theme.colors.table, '--malachite-table', (key, value) =>
            target.style.setProperty(key, value),
        );

        // Add text color properties
        Object.entries(theme.colors.text).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            if (typeof value === 'object' && value !== null) {
                // Handle nested objects like rightSideText (supports one extra nesting level)
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                    const nestedKebabKey = nestedKey.replace(/([A-Z])/g, '-$1').toLowerCase();
                    if (typeof nestedValue === 'object' && nestedValue !== null) {
                        Object.entries(nestedValue as Record<string, string>).forEach(
                            ([deepKey, deepValue]) => {
                                const deepKebabKey = deepKey
                                    .replace(/([A-Z])/g, '-$1')
                                    .toLowerCase();
                                target.style.setProperty(
                                    `--malachite-text-${kebabKey}-${nestedKebabKey}-${deepKebabKey}`,
                                    deepValue,
                                );
                            },
                        );
                    } else {
                        target.style.setProperty(
                            `--malachite-text-${kebabKey}-${nestedKebabKey}`,
                            nestedValue as string,
                        );
                    }
                });
            } else {
                target.style.setProperty(`--malachite-text-${kebabKey}`, value);
            }
        });

        // Add codeBlock color properties
        Object.entries(theme.colors.codeBlock).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-codeblock-${kebabKey}`, value);
        });

        // Add chat color properties
        Object.entries(theme.colors.chat).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-chat-${kebabKey}`, value);
        });

        // Add assistant color properties
        Object.entries(theme.colors.assistant).forEach(([key, value]) => {
            const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            target.style.setProperty(`--malachite-assistant-${kebabKey}`, value);
        });

        const colorVariants = [
            'primary',
            'secondary',
            'outline',
            'destructive',
            'success',
            'warning',
            'error',
        ] as const;
        colorVariants.forEach((variant) => {
            const variantColors = theme.colors[variant];
            Object.entries(variantColors).forEach(([key, value]) => {
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                target.style.setProperty(`--malachite-${variant}-${kebabKey}`, value);
            });
        });
    }
}

/**
 * Apply current theme to a specific shadow root
 */
function applyCurrentThemeToShadowRoot(shadowRoot: ShadowRoot): void {
    try {
        const currentMode = getCurrentThemeMode();
        const customTheme = getCustomTheme();

        // Start with base theme (light or dark)
        const baseTheme = currentMode === 'dark' ? darkTheme : defaultTheme;

        // Merge with custom theme if any
        const finalTheme = customTheme ? mergeThemes(baseTheme, customTheme) : baseTheme;

        applyCSSCustomProperties(finalTheme, shadowRoot);
    } catch (error) {
        console.error('Error applying theme to shadow root:', error);
    }
}

/**
 * Get current theme mode from localStorage
 */
export function getCurrentThemeMode(): 'light' | 'dark' {
    if (typeof window === 'undefined') return 'light';
    return (localStorage.getItem(THEME_MODE_KEY) as 'light' | 'dark') || 'light';
}

/**
 * Get custom theme from localStorage
 */
function getCustomTheme(): PartialMalachiteTheme | null {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(CUSTOM_THEME_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored);
    } catch {
        // Corrupt entry: drop it rather than throwing out of every caller. applyLightTheme
        // used to removeItem before reading, so it self-healed; now that it merges like
        // applyDarkTheme, that recovery has to live here or a bad entry wedges both modes.
        localStorage.removeItem(CUSTOM_THEME_KEY);
        return null;
    }
}

/**
 * Save theme mode to localStorage
 */
function saveThemeMode(mode: 'light' | 'dark'): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_MODE_KEY, mode);
    }
}

/**
 * Save custom theme to localStorage
 */
function saveCustomTheme(theme: PartialMalachiteTheme): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(CUSTOM_THEME_KEY, JSON.stringify(theme));
    }
}

/**
 * Merge themes (deep merge for nested objects)
 */
function mergeThemes(base: MalachiteTheme, customInput: PartialMalachiteTheme): MalachiteTheme {
    // The one choke point every custom-theme path funnels through.
    const custom = sanitizeThemeInput(customInput, '') as PartialMalachiteTheme;

    const result = { ...base };

    if (custom.fontSizes) {
        result.fontSizes = { ...result.fontSizes, ...custom.fontSizes };
    }

    if (custom.iconSizes) {
        result.iconSizes = { ...result.iconSizes, ...custom.iconSizes };
    }

    if (custom.radius) {
        result.radius = { ...result.radius, ...custom.radius };
    }

    if (custom.controlHeights) {
        result.controlHeights = {
            ...(result.controlHeights ?? CONTROL_HEIGHT),
            ...custom.controlHeights,
        };
    }

    if (custom.shadows) {
        result.shadows = { ...result.shadows, ...custom.shadows };
    }

    if (custom.colors) {
        result.colors = { ...result.colors };

        if (custom.colors.shadowColor !== undefined) {
            result.colors.shadowColor = custom.colors.shadowColor;
        }
        if (custom.colors.shadowOpacity !== undefined) {
            result.colors.shadowOpacity = custom.colors.shadowOpacity;
        }

        // Handle card colors
        if (custom.colors.card) {
            result.colors.card = { ...result.colors.card, ...custom.colors.card };
        }

        // Handle overlay colors
        if (custom.colors.overlay) {
            result.colors.overlay = { ...result.colors.overlay, ...custom.colors.overlay };
        }

        // Handle alert colors. Keyed loop rather than a fixed destructure, so an alert
        // variant added to the theme later is merged instead of silently dropped.
        if (custom.colors.alert) {
            // Copy first: the spread makes a fresh `alert`, but each variant still points
            // at defaultTheme's object, which must not be mutated.
            result.colors.alert = { ...result.colors.alert };
            for (const key of Object.keys(
                custom.colors.alert,
            ) as (keyof MalachiteTheme['colors']['alert'])[]) {
                const variant = custom.colors.alert[key];
                if (variant) {
                    result.colors.alert[key] = { ...result.colors.alert[key], ...variant };
                }
            }
        }

        // Handle tooltip colors
        if (custom.colors.tooltip) {
            result.colors.tooltip = { ...result.colors.tooltip, ...custom.colors.tooltip };
        }

        // Handle each color variant explicitly to avoid TypeScript issues
        if (custom.colors.primary) {
            result.colors.primary = { ...result.colors.primary, ...custom.colors.primary };
        }
        if (custom.colors.accent) {
            result.colors.accent = {
                ...(result.colors.accent ?? DEFAULT_ACCENT_COLORS),
                ...custom.colors.accent,
            };
        }
        if (custom.colors.control) {
            result.colors.control = { ...result.colors.control, ...custom.colors.control };
        }
        if (custom.colors.outline) {
            result.colors.outline = { ...result.colors.outline, ...custom.colors.outline };
        }
        if (custom.colors.secondary) {
            result.colors.secondary = { ...result.colors.secondary, ...custom.colors.secondary };
        }
        if (custom.colors.destructive) {
            result.colors.destructive = {
                ...result.colors.destructive,
                ...custom.colors.destructive,
            };
        }
        if (custom.colors.success) {
            result.colors.success = { ...result.colors.success, ...custom.colors.success };
        }
        if (custom.colors.warning) {
            result.colors.warning = { ...result.colors.warning, ...custom.colors.warning };
        }
        if (custom.colors.error) {
            result.colors.error = { ...result.colors.error, ...custom.colors.error };
        }
        if (custom.colors.buttonList) {
            result.colors.buttonList = { ...result.colors.buttonList, ...custom.colors.buttonList };
        }
        if (custom.colors.pillButton) {
            result.colors.pillButton = { ...result.colors.pillButton, ...custom.colors.pillButton };
        }
        if (custom.colors.toggle) {
            result.colors.toggle = { ...result.colors.toggle, ...custom.colors.toggle };
        }
        if (custom.colors.segmentedControl) {
            result.colors.segmentedControl = {
                ...result.colors.segmentedControl,
                ...custom.colors.segmentedControl,
            };
        }
        if (custom.colors.selectableCard) {
            result.colors.selectableCard = {
                ...result.colors.selectableCard,
                ...custom.colors.selectableCard,
            };
        }
        if (custom.colors.cardStrip) {
            result.colors.cardStrip = { ...result.colors.cardStrip, ...custom.colors.cardStrip };
        }
        if (custom.colors.selectionBar) {
            result.colors.selectionBar = {
                ...result.colors.selectionBar,
                ...custom.colors.selectionBar,
            };
        }
        if (custom.colors.inlineConfirm) {
            result.colors.inlineConfirm = {
                ...result.colors.inlineConfirm,
                ...custom.colors.inlineConfirm,
            };
        }
        if (custom.colors.dropdown) {
            result.colors.dropdown = { ...result.colors.dropdown, ...custom.colors.dropdown };
        }
        if (custom.colors.tag) {
            // Copy first: the spread makes a fresh `tag`, but each variant still points at
            // defaultTheme's object, which must not be mutated.
            result.colors.tag = { ...result.colors.tag };
            for (const key of Object.keys(
                custom.colors.tag,
            ) as (keyof MalachiteTheme['colors']['tag'])[]) {
                const variant = custom.colors.tag[key];
                if (variant) {
                    result.colors.tag[key] = { ...result.colors.tag[key], ...variant };
                }
            }
        }
        if (custom.colors.numberTag) {
            result.colors.numberTag = { ...result.colors.numberTag, ...custom.colors.numberTag };
        }
        if (custom.colors.text) {
            const { rightSideText, ...flat } = custom.colors.text;
            result.colors.text = { ...result.colors.text, ...flat };
            if (rightSideText) {
                result.colors.text.rightSideText = {
                    ...result.colors.text.rightSideText,
                    ...rightSideText,
                };
            }
        }
        if (custom.colors.codeBlock) {
            result.colors.codeBlock = { ...result.colors.codeBlock, ...custom.colors.codeBlock };
        }
        if (custom.colors.chat) {
            result.colors.chat = { ...result.colors.chat, ...custom.colors.chat };
        }
        if (custom.colors.assistant) {
            result.colors.assistant = { ...result.colors.assistant, ...custom.colors.assistant };
        }
        // Deeper than the flat groups above: table's sub-objects are nested, so a shallow
        // spread would drop whatever the caller didn't restate.
        if (custom.colors.table) {
            const { header, row, borderedRow, rowVariant, error, ...flat } = custom.colors.table;
            result.colors.table = { ...result.colors.table, ...flat };
            if (header) {
                result.colors.table.header = { ...result.colors.table.header, ...header };
            }
            if (row) {
                result.colors.table.row = { ...result.colors.table.row, ...row };
            }
            if (borderedRow) {
                result.colors.table.borderedRow = {
                    ...result.colors.table.borderedRow,
                    ...borderedRow,
                };
            }
            if (error) {
                result.colors.table.error = { ...result.colors.table.error, ...error };
            }
            if (rowVariant) {
                // Copy first: the spread made a fresh `table`, but `rowVariant` still points at
                // defaultTheme's, which must not be mutated.
                const merged = { ...result.colors.table.rowVariant };
                for (const key of Object.keys(rowVariant) as (keyof typeof rowVariant)[]) {
                    const variant = rowVariant[key];
                    if (variant) {
                        merged[key] = { ...merged[key], ...variant };
                    }
                }
                result.colors.table.rowVariant = merged;
            }
        }
    }

    return result;
}

/**
 * Apply a custom theme to Malachite components
 * This function sets CSS custom properties that will be used by all Malachite components
 * Works with Shadow DOM isolation by applying to all registered shadow roots
 */
export function applyTheme(theme: PartialMalachiteTheme): void {
    // Save custom theme to localStorage
    saveCustomTheme(theme);

    // Apply to main document
    const currentMode = getCurrentThemeMode();
    const baseTheme = currentMode === 'dark' ? darkTheme : defaultTheme;
    const finalTheme = mergeThemes(baseTheme, theme);

    applyCSSCustomProperties(finalTheme, document.documentElement);

    // Apply to all registered shadow roots
    registeredShadowRoots.forEach((shadowRoot) => {
        applyCSSCustomProperties(finalTheme, shadowRoot);
    });

    // Update data-theme attribute for main document CSS
    if (currentMode === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
    } else {
        document.documentElement.removeAttribute('data-theme');
    }
}

/** Scope the active theme (base mode + any persisted custom overrides) to one element/root, without changing global mode. Used by <ThemeScope>. */
export function applyThemeVars(target: HTMLElement | ShadowRoot, mode: ThemeMode): void {
    const baseTheme = mode === 'dark' ? darkTheme : defaultTheme;
    const customTheme = getCustomTheme();
    const finalTheme = customTheme ? mergeThemes(baseTheme, customTheme) : baseTheme;
    applyCSSCustomProperties(finalTheme, target);
}

/**
 * Apply the default (light) theme
 */
export function applyLightTheme(): void {
    saveThemeMode('light');
    document.documentElement.removeAttribute('data-theme');

    // Apply the light theme, preserving any custom theme. Deliberately symmetric with
    // applyDarkTheme: this used to removeItem(CUSTOM_THEME_KEY), so a toggle to light
    // destroyed a consumer's custom theme permanently. resetTheme is the way to clear one.
    const customTheme = getCustomTheme();
    const finalTheme = customTheme ? mergeThemes(defaultTheme, customTheme) : defaultTheme;

    applyCSSCustomProperties(finalTheme, document.documentElement);

    // Apply to all registered shadow roots
    registeredShadowRoots.forEach((shadowRoot) => {
        applyCSSCustomProperties(finalTheme, shadowRoot);
    });
}

/**
 * Apply the dark theme
 */
export function applyDarkTheme(): void {
    saveThemeMode('dark');
    document.documentElement.setAttribute('data-theme', 'dark');

    // Apply dark theme, preserving any custom theme
    const customTheme = getCustomTheme();
    const finalTheme = customTheme ? mergeThemes(darkTheme, customTheme) : darkTheme;

    applyCSSCustomProperties(finalTheme, document.documentElement);

    // Apply to all registered shadow roots
    registeredShadowRoots.forEach((shadowRoot) => {
        applyCSSCustomProperties(finalTheme, shadowRoot);
    });
}

/**
 * Toggle between light and dark themes
 */
export function toggleTheme(): 'light' | 'dark' {
    const currentMode = getCurrentThemeMode();
    if (currentMode === 'dark') {
        applyLightTheme();
        return 'light';
    } else {
        applyDarkTheme();
        return 'dark';
    }
}

/**
 * Apply a custom theme while preserving the current theme mode (light/dark)
 */
export function applyCustomTheme(customTheme: PartialMalachiteTheme): void {
    applyTheme(customTheme);
}

/**
 * Reset theme to default values
 */
export function resetTheme(): void {
    const currentMode = getCurrentThemeMode();
    localStorage.removeItem(CUSTOM_THEME_KEY);

    if (currentMode === 'dark') {
        applyDarkTheme();
    } else {
        applyLightTheme();
    }
}

/**
 * Initialize the theme system - should be called on app startup
 */
export function initializeTheme(): void {
    if (typeof window !== 'undefined') {
        const currentMode = getCurrentThemeMode();
        const customTheme = getCustomTheme();

        if (currentMode === 'dark') {
            if (customTheme) {
                const finalTheme = mergeThemes(darkTheme, customTheme);
                applyCSSCustomProperties(finalTheme, document.documentElement);
            } else {
                applyCSSCustomProperties(darkTheme, document.documentElement);
            }
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            if (customTheme) {
                const finalTheme = mergeThemes(defaultTheme, customTheme);
                applyCSSCustomProperties(finalTheme, document.documentElement);
            } else {
                applyCSSCustomProperties(defaultTheme, document.documentElement);
            }
            document.documentElement.removeAttribute('data-theme');
        }
    }
}

/**
 * Get current theme values from CSS custom properties
 */
export function getCurrentTheme(): MalachiteTheme {
    const computedStyle = getComputedStyle(document.documentElement);

    return {
        fontSizes: {
            xsmall:
                computedStyle.getPropertyValue('--malachite-font-size-xsmall').trim() ||
                defaultTheme.fontSizes.xsmall,
            small:
                computedStyle.getPropertyValue('--malachite-font-size-small').trim() ||
                defaultTheme.fontSizes.small,
            medium:
                computedStyle.getPropertyValue('--malachite-font-size-medium').trim() ||
                defaultTheme.fontSizes.medium,
            '15':
                computedStyle.getPropertyValue('--malachite-font-size-15').trim() ||
                defaultTheme.fontSizes['15'],
            large:
                computedStyle.getPropertyValue('--malachite-font-size-large').trim() ||
                defaultTheme.fontSizes.large,
            xlarge:
                computedStyle.getPropertyValue('--malachite-font-size-xlarge').trim() ||
                defaultTheme.fontSizes.xlarge,
            xxlarge:
                computedStyle.getPropertyValue('--malachite-font-size-xxlarge').trim() ||
                defaultTheme.fontSizes.xxlarge,
        },
        iconSizes: {
            xsmall:
                computedStyle.getPropertyValue('--malachite-icon-size-xsmall').trim() ||
                defaultTheme.iconSizes.xsmall,
            small:
                computedStyle.getPropertyValue('--malachite-icon-size-small').trim() ||
                defaultTheme.iconSizes.small,
            medium:
                computedStyle.getPropertyValue('--malachite-icon-size-medium').trim() ||
                defaultTheme.iconSizes.medium,
            large:
                computedStyle.getPropertyValue('--malachite-icon-size-large').trim() ||
                defaultTheme.iconSizes.large,
        },
        controlHeights: {
            small:
                computedStyle.getPropertyValue('--malachite-control-height-small').trim() ||
                CONTROL_HEIGHT.small,
            medium:
                computedStyle.getPropertyValue('--malachite-control-height-medium').trim() ||
                CONTROL_HEIGHT.medium,
            large:
                computedStyle.getPropertyValue('--malachite-control-height-large').trim() ||
                CONTROL_HEIGHT.large,
        },
        radius: {
            xs:
                computedStyle.getPropertyValue('--malachite-radius-xs').trim() ||
                defaultTheme.radius.xs,
            sm:
                computedStyle.getPropertyValue('--malachite-radius-sm').trim() ||
                defaultTheme.radius.sm,
            md:
                computedStyle.getPropertyValue('--malachite-radius-md').trim() ||
                defaultTheme.radius.md,
            lg:
                computedStyle.getPropertyValue('--malachite-radius-lg').trim() ||
                defaultTheme.radius.lg,
            '10':
                computedStyle.getPropertyValue('--malachite-radius-10').trim() ||
                defaultTheme.radius['10'],
            xl:
                computedStyle.getPropertyValue('--malachite-radius-xl').trim() ||
                defaultTheme.radius.xl,
            xxl:
                computedStyle.getPropertyValue('--malachite-radius-xxl').trim() ||
                defaultTheme.radius.xxl,
            xxxl:
                computedStyle.getPropertyValue('--malachite-radius-xxxl').trim() ||
                defaultTheme.radius.xxxl,
        },
        shadows: {
            sm:
                computedStyle.getPropertyValue('--malachite-shadow-sm').trim() ||
                defaultTheme.shadows.sm,
            md:
                computedStyle.getPropertyValue('--malachite-shadow-md').trim() ||
                defaultTheme.shadows.md,
            lg:
                computedStyle.getPropertyValue('--malachite-shadow-lg').trim() ||
                defaultTheme.shadows.lg,
            xl:
                computedStyle.getPropertyValue('--malachite-shadow-xl').trim() ||
                defaultTheme.shadows.xl,
            glow:
                computedStyle.getPropertyValue('--malachite-shadow-glow').trim() ||
                defaultTheme.shadows.glow,
            glowLg:
                computedStyle.getPropertyValue('--malachite-shadow-glow-lg').trim() ||
                defaultTheme.shadows.glowLg,
            default:
                computedStyle.getPropertyValue('--malachite-shadow-default').trim() ||
                defaultTheme.shadows.default,
            accent:
                computedStyle.getPropertyValue('--malachite-shadow-accent').trim() ||
                DEFAULT_ACCENT_SHADOW,
            success:
                computedStyle.getPropertyValue('--malachite-shadow-success').trim() ||
                defaultTheme.shadows.success,
            warning:
                computedStyle.getPropertyValue('--malachite-shadow-warning').trim() ||
                defaultTheme.shadows.warning,
            error:
                computedStyle.getPropertyValue('--malachite-shadow-error').trim() ||
                defaultTheme.shadows.error,
        },
        colors: {
            shadowColor:
                computedStyle.getPropertyValue('--malachite-shadow-color').trim() ||
                defaultTheme.colors.shadowColor,
            shadowOpacity:
                parseFloat(computedStyle.getPropertyValue('--malachite-shadow-opacity').trim()) ||
                defaultTheme.colors.shadowOpacity,
            card: {
                background:
                    computedStyle.getPropertyValue('--malachite-card-background').trim() ||
                    defaultTheme.colors.card.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-card-foreground').trim() ||
                    defaultTheme.colors.card.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-card-border').trim() ||
                    defaultTheme.colors.card.border,
                shadow:
                    computedStyle.getPropertyValue('--malachite-card-shadow').trim() ||
                    defaultTheme.colors.card.shadow,
                dimmedBackground:
                    computedStyle.getPropertyValue('--malachite-card-dimmed-background').trim() ||
                    defaultTheme.colors.card.dimmedBackground,
                sunkenBackground:
                    computedStyle.getPropertyValue('--malachite-card-sunken-background').trim() ||
                    defaultTheme.colors.card.sunkenBackground,
                warmBackground:
                    computedStyle.getPropertyValue('--malachite-card-warm-background').trim() ||
                    defaultTheme.colors.card.warmBackground,
                headerBackground:
                    computedStyle.getPropertyValue('--malachite-card-header-background').trim() ||
                    defaultTheme.colors.card.headerBackground,
                footerBackground:
                    computedStyle.getPropertyValue('--malachite-card-footer-background').trim() ||
                    defaultTheme.colors.card.footerBackground,
                footerGlassBackground:
                    computedStyle
                        .getPropertyValue('--malachite-card-footer-glass-background')
                        .trim() || defaultTheme.colors.card.footerGlassBackground,
                trayNeutral:
                    computedStyle.getPropertyValue('--malachite-card-tray-neutral').trim() ||
                    defaultTheme.colors.card.trayNeutral,
                trayWarning:
                    computedStyle.getPropertyValue('--malachite-card-tray-warning').trim() ||
                    defaultTheme.colors.card.trayWarning,
                trayError:
                    computedStyle.getPropertyValue('--malachite-card-tray-error').trim() ||
                    defaultTheme.colors.card.trayError,
                traySuccess:
                    computedStyle.getPropertyValue('--malachite-card-tray-success').trim() ||
                    defaultTheme.colors.card.traySuccess,
                subtleDivider:
                    computedStyle.getPropertyValue('--malachite-card-subtle-divider').trim() ||
                    defaultTheme.colors.card.subtleDivider,
            },
            overlay: {
                scrim:
                    computedStyle.getPropertyValue('--malachite-overlay-scrim').trim() ||
                    defaultTheme.colors.overlay.scrim,
            },
            alert: {
                info: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-alert-info-background')
                            .trim() || defaultTheme.colors.alert.info.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-alert-info-foreground')
                            .trim() || defaultTheme.colors.alert.info.foreground,
                },
                success: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-alert-success-background')
                            .trim() || defaultTheme.colors.alert.success.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-alert-success-foreground')
                            .trim() || defaultTheme.colors.alert.success.foreground,
                },
                error: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-alert-error-background')
                            .trim() || defaultTheme.colors.alert.error.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-alert-error-foreground')
                            .trim() || defaultTheme.colors.alert.error.foreground,
                },
                warning: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-alert-warning-background')
                            .trim() || defaultTheme.colors.alert.warning.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-alert-warning-foreground')
                            .trim() || defaultTheme.colors.alert.warning.foreground,
                },
                assistant: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-alert-assistant-background')
                            .trim() || defaultTheme.colors.alert.assistant.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-alert-assistant-foreground')
                            .trim() || defaultTheme.colors.alert.assistant.foreground,
                },
            },
            tooltip: {
                background:
                    computedStyle.getPropertyValue('--malachite-tooltip-background').trim() ||
                    defaultTheme.colors.tooltip.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-tooltip-foreground').trim() ||
                    defaultTheme.colors.tooltip.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-tooltip-border').trim() ||
                    defaultTheme.colors.tooltip.border,
                shadow:
                    computedStyle.getPropertyValue('--malachite-tooltip-shadow').trim() ||
                    defaultTheme.colors.tooltip.shadow,
            },
            primary: {
                background:
                    computedStyle.getPropertyValue('--malachite-primary-background').trim() ||
                    defaultTheme.colors.primary.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-primary-foreground').trim() ||
                    defaultTheme.colors.primary.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-primary-border').trim() ||
                    defaultTheme.colors.primary.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-primary-border-hover').trim() ||
                    defaultTheme.colors.primary.borderHover,
            },
            accent: {
                background:
                    computedStyle.getPropertyValue('--malachite-accent-background').trim() ||
                    DEFAULT_ACCENT_COLORS.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-accent-foreground').trim() ||
                    DEFAULT_ACCENT_COLORS.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-accent-border').trim() ||
                    DEFAULT_ACCENT_COLORS.border,
                backgroundGlass:
                    computedStyle.getPropertyValue('--malachite-accent-background-glass').trim() ||
                    DEFAULT_ACCENT_COLORS.backgroundGlass,
            },
            control: {
                borderHover:
                    computedStyle.getPropertyValue('--malachite-control-border-hover').trim() ||
                    defaultTheme.colors.control.borderHover,
            },
            outline: {
                foreground:
                    computedStyle.getPropertyValue('--malachite-outline-foreground').trim() ||
                    defaultTheme.colors.outline.foreground,
            },
            secondary: {
                background:
                    computedStyle.getPropertyValue('--malachite-secondary-background').trim() ||
                    defaultTheme.colors.secondary.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-secondary-foreground').trim() ||
                    defaultTheme.colors.secondary.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-secondary-border').trim() ||
                    defaultTheme.colors.secondary.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-secondary-border-hover').trim() ||
                    defaultTheme.colors.secondary.borderHover,
            },
            destructive: {
                background:
                    computedStyle.getPropertyValue('--malachite-destructive-background').trim() ||
                    defaultTheme.colors.destructive.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-destructive-foreground').trim() ||
                    defaultTheme.colors.destructive.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-destructive-border').trim() ||
                    defaultTheme.colors.destructive.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-destructive-border-hover').trim() ||
                    defaultTheme.colors.destructive.borderHover,
                shadow:
                    computedStyle.getPropertyValue('--malachite-destructive-shadow').trim() ||
                    defaultTheme.colors.destructive.shadow,
                shadowLight:
                    computedStyle.getPropertyValue('--malachite-destructive-shadow-light').trim() ||
                    defaultTheme.colors.destructive.shadowLight,
                shadowMedium:
                    computedStyle
                        .getPropertyValue('--malachite-destructive-shadow-medium')
                        .trim() || defaultTheme.colors.destructive.shadowMedium,
            },
            success: {
                background:
                    computedStyle.getPropertyValue('--malachite-success-background').trim() ||
                    defaultTheme.colors.success.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-success-foreground').trim() ||
                    defaultTheme.colors.success.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-success-border').trim() ||
                    defaultTheme.colors.success.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-success-border-hover').trim() ||
                    defaultTheme.colors.success.borderHover,
                backgroundGlass:
                    computedStyle.getPropertyValue('--malachite-success-background-glass').trim() ||
                    defaultTheme.colors.success.backgroundGlass,
                shadowLight:
                    computedStyle.getPropertyValue('--malachite-success-shadow-light').trim() ||
                    defaultTheme.colors.success.shadowLight,
                shadowMedium:
                    computedStyle.getPropertyValue('--malachite-success-shadow-medium').trim() ||
                    defaultTheme.colors.success.shadowMedium,
            },
            warning: {
                background:
                    computedStyle.getPropertyValue('--malachite-warning-background').trim() ||
                    defaultTheme.colors.warning.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-warning-foreground').trim() ||
                    defaultTheme.colors.warning.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-warning-border').trim() ||
                    defaultTheme.colors.warning.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-warning-border-hover').trim() ||
                    defaultTheme.colors.warning.borderHover,
                backgroundGlass:
                    computedStyle.getPropertyValue('--malachite-warning-background-glass').trim() ||
                    defaultTheme.colors.warning.backgroundGlass,
                shadowLight:
                    computedStyle.getPropertyValue('--malachite-warning-shadow-light').trim() ||
                    defaultTheme.colors.warning.shadowLight,
                shadowMedium:
                    computedStyle.getPropertyValue('--malachite-warning-shadow-medium').trim() ||
                    defaultTheme.colors.warning.shadowMedium,
            },
            error: {
                background:
                    computedStyle.getPropertyValue('--malachite-error-background').trim() ||
                    defaultTheme.colors.error.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-error-foreground').trim() ||
                    defaultTheme.colors.error.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-error-border').trim() ||
                    defaultTheme.colors.error.border,
                borderHover:
                    computedStyle.getPropertyValue('--malachite-error-border-hover').trim() ||
                    defaultTheme.colors.error.borderHover,
                backgroundGlass:
                    computedStyle.getPropertyValue('--malachite-error-background-glass').trim() ||
                    defaultTheme.colors.error.backgroundGlass,
                shadowLight:
                    computedStyle.getPropertyValue('--malachite-error-shadow-light').trim() ||
                    defaultTheme.colors.error.shadowLight,
                shadowMedium:
                    computedStyle.getPropertyValue('--malachite-error-shadow-medium').trim() ||
                    defaultTheme.colors.error.shadowMedium,
            },
            buttonList: {
                background:
                    computedStyle.getPropertyValue('--malachite-buttonlist-background').trim() ||
                    defaultTheme.colors.buttonList.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-buttonlist-foreground').trim() ||
                    defaultTheme.colors.buttonList.foreground,
                hoverBackground:
                    computedStyle
                        .getPropertyValue('--malachite-buttonlist-hover-background')
                        .trim() || defaultTheme.colors.buttonList.hoverBackground,
                hoverForeground:
                    computedStyle
                        .getPropertyValue('--malachite-buttonlist-hover-foreground')
                        .trim() || defaultTheme.colors.buttonList.hoverForeground,
            },
            pillButton: {
                background:
                    computedStyle.getPropertyValue('--malachite-pillbutton-background').trim() ||
                    defaultTheme.colors.pillButton.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-pillbutton-foreground').trim() ||
                    defaultTheme.colors.pillButton.foreground,
                hoverBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-hover-background')
                        .trim() || defaultTheme.colors.pillButton.hoverBackground,
                hoverForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-hover-foreground')
                        .trim() || defaultTheme.colors.pillButton.hoverForeground,
                pressedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-pressed-background')
                        .trim() || defaultTheme.colors.pillButton.pressedBackground,
                pressedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-pressed-foreground')
                        .trim() || defaultTheme.colors.pillButton.pressedForeground,
                pressedOpacity:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-pressed-opacity')
                        .trim() || defaultTheme.colors.pillButton.pressedOpacity,
                activeBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-active-background')
                        .trim() || defaultTheme.colors.pillButton.activeBackground,
                activeForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-active-foreground')
                        .trim() || defaultTheme.colors.pillButton.activeForeground,
                staticBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-static-background')
                        .trim() || defaultTheme.colors.pillButton.staticBackground,
                staticForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-static-foreground')
                        .trim() || defaultTheme.colors.pillButton.staticForeground,
                invertedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-background')
                        .trim() || defaultTheme.colors.pillButton.invertedBackground,
                invertedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-foreground')
                        .trim() || defaultTheme.colors.pillButton.invertedForeground,
                invertedHoverBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-hover-background')
                        .trim() || defaultTheme.colors.pillButton.invertedHoverBackground,
                invertedHoverForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-hover-foreground')
                        .trim() || defaultTheme.colors.pillButton.invertedHoverForeground,
                invertedPressedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-pressed-background')
                        .trim() || defaultTheme.colors.pillButton.invertedPressedBackground,
                invertedPressedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-pillbutton-inverted-pressed-foreground')
                        .trim() || defaultTheme.colors.pillButton.invertedPressedForeground,
            },
            toggle: {
                track:
                    computedStyle.getPropertyValue('--malachite-toggle-track').trim() ||
                    defaultTheme.colors.toggle.track,
                trackChecked:
                    computedStyle.getPropertyValue('--malachite-toggle-track-checked').trim() ||
                    defaultTheme.colors.toggle.trackChecked,
                trackCheckedPending:
                    computedStyle
                        .getPropertyValue('--malachite-toggle-track-checked-pending')
                        .trim() || defaultTheme.colors.toggle.trackCheckedPending,
                knob:
                    computedStyle.getPropertyValue('--malachite-toggle-knob').trim() ||
                    defaultTheme.colors.toggle.knob,
            },
            segmentedControl: {
                trackBackground:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-track-background')
                        .trim() || defaultTheme.colors.segmentedControl.trackBackground,
                itemForeground:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-item-foreground')
                        .trim() || defaultTheme.colors.segmentedControl.itemForeground,
                itemHoverForeground:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-item-hover-foreground')
                        .trim() || defaultTheme.colors.segmentedControl.itemHoverForeground,
                selectedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-selected-background')
                        .trim() || defaultTheme.colors.segmentedControl.selectedBackground,
                selectedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-selected-foreground')
                        .trim() || defaultTheme.colors.segmentedControl.selectedForeground,
                selectedShadow:
                    computedStyle
                        .getPropertyValue('--malachite-segmented-control-selected-shadow')
                        .trim() || defaultTheme.colors.segmentedControl.selectedShadow,
            },
            selectableCard: {
                background:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-background')
                        .trim() || defaultTheme.colors.selectableCard.background,
                border:
                    computedStyle.getPropertyValue('--malachite-selectable-card-border').trim() ||
                    defaultTheme.colors.selectableCard.border,
                selectedTint:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-selected-tint')
                        .trim() || defaultTheme.colors.selectableCard.selectedTint,
                selectedSurfaceEnd:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-selected-surface-end')
                        .trim() || defaultTheme.colors.selectableCard.selectedSurfaceEnd,
                selectedBorder:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-selected-border')
                        .trim() || defaultTheme.colors.selectableCard.selectedBorder,
                librarySelectedTint:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-library-selected-tint')
                        .trim() || defaultTheme.colors.selectableCard.librarySelectedTint,
                librarySelectedBorder:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-library-selected-border')
                        .trim() || defaultTheme.colors.selectableCard.librarySelectedBorder,
                editingTint:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-editing-tint')
                        .trim() || defaultTheme.colors.selectableCard.editingTint,
                accent:
                    computedStyle.getPropertyValue('--malachite-selectable-card-accent').trim() ||
                    defaultTheme.colors.selectableCard.accent,
                mutedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-muted-foreground')
                        .trim() || defaultTheme.colors.selectableCard.mutedForeground,
                hoverBackground:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-hover-background')
                        .trim() || defaultTheme.colors.selectableCard.hoverBackground,
                hoverRule:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-hover-rule')
                        .trim() || defaultTheme.colors.selectableCard.hoverRule,
                pendingForeground:
                    computedStyle
                        .getPropertyValue('--malachite-selectable-card-pending-foreground')
                        .trim() || defaultTheme.colors.selectableCard.pendingForeground,
            },
            cardStrip: {
                background:
                    computedStyle.getPropertyValue('--malachite-card-strip-background').trim() ||
                    defaultTheme.colors.cardStrip.background,
                divider:
                    computedStyle.getPropertyValue('--malachite-card-strip-divider').trim() ||
                    defaultTheme.colors.cardStrip.divider,
                fade:
                    computedStyle.getPropertyValue('--malachite-card-strip-fade').trim() ||
                    defaultTheme.colors.cardStrip.fade,
                arrowBackground:
                    computedStyle
                        .getPropertyValue('--malachite-card-strip-arrow-background')
                        .trim() || defaultTheme.colors.cardStrip.arrowBackground,
                arrowBorder:
                    computedStyle.getPropertyValue('--malachite-card-strip-arrow-border').trim() ||
                    defaultTheme.colors.cardStrip.arrowBorder,
                addPrimaryBackground:
                    computedStyle
                        .getPropertyValue('--malachite-card-strip-add-primary-background')
                        .trim() || defaultTheme.colors.cardStrip.addPrimaryBackground,
                addPrimaryForeground:
                    computedStyle
                        .getPropertyValue('--malachite-card-strip-add-primary-foreground')
                        .trim() || defaultTheme.colors.cardStrip.addPrimaryForeground,
            },
            selectionBar: {
                background:
                    computedStyle.getPropertyValue('--malachite-selection-bar-background').trim() ||
                    defaultTheme.colors.selectionBar.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-selection-bar-foreground').trim() ||
                    defaultTheme.colors.selectionBar.foreground,
                mutedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-selection-bar-muted-foreground')
                        .trim() || defaultTheme.colors.selectionBar.mutedForeground,
                divider:
                    computedStyle.getPropertyValue('--malachite-selection-bar-divider').trim() ||
                    defaultTheme.colors.selectionBar.divider,
                border:
                    computedStyle.getPropertyValue('--malachite-selection-bar-border').trim() ||
                    defaultTheme.colors.selectionBar.border,
                primaryBorder:
                    computedStyle
                        .getPropertyValue('--malachite-selection-bar-primary-border')
                        .trim() || defaultTheme.colors.selectionBar.primaryBorder,
                shadow:
                    computedStyle.getPropertyValue('--malachite-selection-bar-shadow').trim() ||
                    defaultTheme.colors.selectionBar.shadow,
                noticeBackground:
                    computedStyle
                        .getPropertyValue('--malachite-selection-bar-notice-background')
                        .trim() || defaultTheme.colors.selectionBar.noticeBackground,
                noticeForeground:
                    computedStyle
                        .getPropertyValue('--malachite-selection-bar-notice-foreground')
                        .trim() || defaultTheme.colors.selectionBar.noticeForeground,
                noticeShadow:
                    computedStyle
                        .getPropertyValue('--malachite-selection-bar-notice-shadow')
                        .trim() || defaultTheme.colors.selectionBar.noticeShadow,
            },
            inlineConfirm: {
                background:
                    computedStyle
                        .getPropertyValue('--malachite-inline-confirm-background')
                        .trim() || defaultTheme.colors.inlineConfirm.background,
                foreground:
                    computedStyle
                        .getPropertyValue('--malachite-inline-confirm-foreground')
                        .trim() || defaultTheme.colors.inlineConfirm.foreground,
                divider:
                    computedStyle.getPropertyValue('--malachite-inline-confirm-divider').trim() ||
                    defaultTheme.colors.inlineConfirm.divider,
                shadow:
                    computedStyle.getPropertyValue('--malachite-inline-confirm-shadow').trim() ||
                    defaultTheme.colors.inlineConfirm.shadow,
            },
            dropdown: {
                border:
                    computedStyle.getPropertyValue('--malachite-dropdown-border').trim() ||
                    defaultTheme.colors.dropdown.border,
                borderActive:
                    computedStyle.getPropertyValue('--malachite-dropdown-border-active').trim() ||
                    defaultTheme.colors.dropdown.borderActive,
                borderFilter:
                    computedStyle.getPropertyValue('--malachite-dropdown-border-filter').trim() ||
                    defaultTheme.colors.dropdown.borderFilter,
                borderError:
                    computedStyle.getPropertyValue('--malachite-dropdown-border-error').trim() ||
                    defaultTheme.colors.dropdown.borderError,
                borderPending:
                    computedStyle.getPropertyValue('--malachite-dropdown-border-pending').trim() ||
                    defaultTheme.colors.dropdown.borderPending,
                shadow:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow').trim() ||
                    defaultTheme.colors.dropdown.shadow,
                shadowHover:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow-hover').trim() ||
                    defaultTheme.colors.dropdown.shadowHover,
                shadowActive:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow-active').trim() ||
                    defaultTheme.colors.dropdown.shadowActive,
                shadowFilter:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow-filter').trim() ||
                    defaultTheme.colors.dropdown.shadowFilter,
                shadowError:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow-error').trim() ||
                    defaultTheme.colors.dropdown.shadowError,
                shadowPending:
                    computedStyle.getPropertyValue('--malachite-dropdown-shadow-pending').trim() ||
                    defaultTheme.colors.dropdown.shadowPending,
                disabledBackground:
                    computedStyle
                        .getPropertyValue('--malachite-dropdown-disabled-background')
                        .trim() || defaultTheme.colors.dropdown.disabledBackground,
            },
            tag: {
                primary: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-primary-background')
                            .trim() || defaultTheme.colors.tag.primary.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-primary-foreground')
                            .trim() || defaultTheme.colors.tag.primary.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-primary-border').trim() ||
                        defaultTheme.colors.tag.primary.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-primary-shadow').trim() ||
                        defaultTheme.colors.tag.primary.shadow,
                },
                secondary: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-secondary-background')
                            .trim() || defaultTheme.colors.tag.secondary.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-secondary-foreground')
                            .trim() || defaultTheme.colors.tag.secondary.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-secondary-border').trim() ||
                        defaultTheme.colors.tag.secondary.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-secondary-shadow').trim() ||
                        defaultTheme.colors.tag.secondary.shadow,
                },
                default: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-default-background')
                            .trim() || defaultTheme.colors.tag.default.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-default-foreground')
                            .trim() || defaultTheme.colors.tag.default.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-default-border').trim() ||
                        defaultTheme.colors.tag.default.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-default-shadow').trim() ||
                        defaultTheme.colors.tag.default.shadow,
                },
                error: {
                    background:
                        computedStyle.getPropertyValue('--malachite-tag-error-background').trim() ||
                        defaultTheme.colors.tag.error.background,
                    foreground:
                        computedStyle.getPropertyValue('--malachite-tag-error-foreground').trim() ||
                        defaultTheme.colors.tag.error.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-error-border').trim() ||
                        defaultTheme.colors.tag.error.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-error-shadow').trim() ||
                        defaultTheme.colors.tag.error.shadow,
                },
                warning: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-warning-background')
                            .trim() || defaultTheme.colors.tag.warning.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-warning-foreground')
                            .trim() || defaultTheme.colors.tag.warning.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-warning-border').trim() ||
                        defaultTheme.colors.tag.warning.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-warning-shadow').trim() ||
                        defaultTheme.colors.tag.warning.shadow,
                },
                success: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-success-background')
                            .trim() || defaultTheme.colors.tag.success.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-success-foreground')
                            .trim() || defaultTheme.colors.tag.success.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-success-border').trim() ||
                        defaultTheme.colors.tag.success.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-success-shadow').trim() ||
                        defaultTheme.colors.tag.success.shadow,
                },
                emphasis: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-emphasis-background')
                            .trim() || defaultTheme.colors.tag.emphasis.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-emphasis-foreground')
                            .trim() || defaultTheme.colors.tag.emphasis.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-emphasis-border').trim() ||
                        defaultTheme.colors.tag.emphasis.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-emphasis-shadow').trim() ||
                        defaultTheme.colors.tag.emphasis.shadow,
                },
                info: {
                    background:
                        computedStyle.getPropertyValue('--malachite-tag-info-background').trim() ||
                        defaultTheme.colors.tag.info.background,
                    foreground:
                        computedStyle.getPropertyValue('--malachite-tag-info-foreground').trim() ||
                        defaultTheme.colors.tag.info.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-info-border').trim() ||
                        defaultTheme.colors.tag.info.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-info-shadow').trim() ||
                        defaultTheme.colors.tag.info.shadow,
                },
                assistant: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-tag-assistant-background')
                            .trim() || defaultTheme.colors.tag.assistant.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-tag-assistant-foreground')
                            .trim() || defaultTheme.colors.tag.assistant.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-tag-assistant-border').trim() ||
                        defaultTheme.colors.tag.assistant.border,
                    shadow:
                        computedStyle.getPropertyValue('--malachite-tag-assistant-shadow').trim() ||
                        defaultTheme.colors.tag.assistant.shadow,
                },
            },
            numberTag: {
                background:
                    computedStyle.getPropertyValue('--malachite-number-tag-background').trim() ||
                    defaultTheme.colors.numberTag.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-number-tag-foreground').trim() ||
                    defaultTheme.colors.numberTag.foreground,
                emphasisBackground:
                    computedStyle
                        .getPropertyValue('--malachite-number-tag-emphasis-background')
                        .trim() || defaultTheme.colors.numberTag.emphasisBackground,
                emphasisForeground:
                    computedStyle
                        .getPropertyValue('--malachite-number-tag-emphasis-foreground')
                        .trim() || defaultTheme.colors.numberTag.emphasisForeground,
            },
            text: {
                primary:
                    computedStyle.getPropertyValue('--malachite-text-primary').trim() ||
                    defaultTheme.colors.text.primary,
                secondary:
                    computedStyle.getPropertyValue('--malachite-text-secondary').trim() ||
                    defaultTheme.colors.text.secondary,
                dimmed:
                    computedStyle.getPropertyValue('--malachite-text-dimmed').trim() ||
                    defaultTheme.colors.text.dimmed,
                emphasis:
                    computedStyle.getPropertyValue('--malachite-text-emphasis').trim() ||
                    defaultTheme.colors.text.emphasis,
                muted:
                    computedStyle.getPropertyValue('--malachite-text-muted').trim() ||
                    defaultTheme.colors.text.muted,
                placeholder:
                    computedStyle.getPropertyValue('--malachite-text-placeholder').trim() ||
                    defaultTheme.colors.text.placeholder,
                destructive:
                    computedStyle.getPropertyValue('--malachite-text-destructive').trim() ||
                    defaultTheme.colors.text.destructive,
                success:
                    computedStyle.getPropertyValue('--malachite-text-success').trim() ||
                    defaultTheme.colors.text.success,
                highlight:
                    computedStyle.getPropertyValue('--malachite-text-highlight').trim() ||
                    defaultTheme.colors.text.highlight,
                warning:
                    computedStyle.getPropertyValue('--malachite-text-warning').trim() ||
                    defaultTheme.colors.text.warning,
                warningMuted:
                    computedStyle.getPropertyValue('--malachite-text-warning-muted').trim() ||
                    defaultTheme.colors.text.warningMuted,
                error:
                    computedStyle.getPropertyValue('--malachite-text-error').trim() ||
                    defaultTheme.colors.text.error,
                info:
                    computedStyle.getPropertyValue('--malachite-text-info').trim() ||
                    defaultTheme.colors.text.info,
                linkForeground:
                    computedStyle.getPropertyValue('--malachite-text-link-foreground').trim() ||
                    defaultTheme.colors.text.linkForeground,
                linkForegroundHover:
                    computedStyle
                        .getPropertyValue('--malachite-text-link-foreground-hover')
                        .trim() || defaultTheme.colors.text.linkForegroundHover,
                rightSideText: {
                    muted:
                        computedStyle
                            .getPropertyValue('--malachite-text-right-side-text-muted')
                            .trim() || defaultTheme.colors.text.rightSideText.muted,
                    primary:
                        computedStyle
                            .getPropertyValue('--malachite-text-right-side-text-primary')
                            .trim() || defaultTheme.colors.text.rightSideText.primary,
                    alignment:
                        (computedStyle
                            .getPropertyValue('--malachite-text-right-side-text-alignment')
                            .trim() as 'left' | 'right') ||
                        defaultTheme.colors.text.rightSideText.alignment,
                    fontSize:
                        computedStyle
                            .getPropertyValue('--malachite-text-right-side-text-font-size')
                            .trim() || defaultTheme.colors.text.rightSideText.fontSize,
                    minWidth:
                        computedStyle
                            .getPropertyValue('--malachite-text-right-side-text-min-width')
                            .trim() || defaultTheme.colors.text.rightSideText.minWidth,
                },
            },
            codeBlock: {
                background:
                    computedStyle.getPropertyValue('--malachite-codeblock-background').trim() ||
                    defaultTheme.colors.codeBlock.background,
                foreground:
                    computedStyle.getPropertyValue('--malachite-codeblock-foreground').trim() ||
                    defaultTheme.colors.codeBlock.foreground,
                border:
                    computedStyle.getPropertyValue('--malachite-codeblock-border').trim() ||
                    defaultTheme.colors.codeBlock.border,
                lineNumber:
                    computedStyle.getPropertyValue('--malachite-codeblock-line-number').trim() ||
                    defaultTheme.colors.codeBlock.lineNumber,
                keyword:
                    computedStyle.getPropertyValue('--malachite-codeblock-keyword').trim() ||
                    defaultTheme.colors.codeBlock.keyword,
                comment:
                    computedStyle.getPropertyValue('--malachite-codeblock-comment').trim() ||
                    defaultTheme.colors.codeBlock.comment,
                string:
                    computedStyle.getPropertyValue('--malachite-codeblock-string').trim() ||
                    defaultTheme.colors.codeBlock.string,
                number:
                    computedStyle.getPropertyValue('--malachite-codeblock-number').trim() ||
                    defaultTheme.colors.codeBlock.number,
                type:
                    computedStyle.getPropertyValue('--malachite-codeblock-type').trim() ||
                    defaultTheme.colors.codeBlock.type,
                diffAddedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-added-background')
                        .trim() || defaultTheme.colors.codeBlock.diffAddedBackground,
                diffRemovedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-removed-background')
                        .trim() || defaultTheme.colors.codeBlock.diffRemovedBackground,
                diffAssistantBackground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-assistant-background')
                        .trim() || defaultTheme.colors.codeBlock.diffAssistantBackground,
                diffAssistantForeground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-assistant-foreground')
                        .trim() || defaultTheme.colors.codeBlock.diffAssistantForeground,
                diffAssistantRemovedBackground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-assistant-removed-background')
                        .trim() || defaultTheme.colors.codeBlock.diffAssistantRemovedBackground,
                diffRemovedForeground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-removed-foreground')
                        .trim() || defaultTheme.colors.codeBlock.diffRemovedForeground,
                diffContextForeground:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-context-foreground')
                        .trim() || defaultTheme.colors.codeBlock.diffContextForeground,
                diffContextText:
                    computedStyle
                        .getPropertyValue('--malachite-codeblock-diff-context-text')
                        .trim() || defaultTheme.colors.codeBlock.diffContextText,
            },
            chat: {
                userBubbleBackground:
                    computedStyle
                        .getPropertyValue('--malachite-chat-user-bubble-background')
                        .trim() || defaultTheme.colors.chat.userBubbleBackground,
                userBubbleBorder:
                    computedStyle.getPropertyValue('--malachite-chat-user-bubble-border').trim() ||
                    defaultTheme.colors.chat.userBubbleBorder,
                assistantBubbleBackground:
                    computedStyle
                        .getPropertyValue('--malachite-chat-assistant-bubble-background')
                        .trim() || defaultTheme.colors.chat.assistantBubbleBackground,
                assistantBubbleBorder:
                    computedStyle
                        .getPropertyValue('--malachite-chat-assistant-bubble-border')
                        .trim() || defaultTheme.colors.chat.assistantBubbleBorder,
                assistantBubbleForeground:
                    computedStyle
                        .getPropertyValue('--malachite-chat-assistant-bubble-foreground')
                        .trim() || defaultTheme.colors.chat.assistantBubbleForeground,
            },
            assistant: {
                foreground:
                    computedStyle.getPropertyValue('--malachite-assistant-foreground').trim() ||
                    defaultTheme.colors.assistant.foreground,
                surfaceBackground:
                    computedStyle
                        .getPropertyValue('--malachite-assistant-surface-background')
                        .trim() || defaultTheme.colors.assistant.surfaceBackground,
                surfaceBorder:
                    computedStyle.getPropertyValue('--malachite-assistant-surface-border').trim() ||
                    defaultTheme.colors.assistant.surfaceBorder,
                surfaceForeground:
                    computedStyle
                        .getPropertyValue('--malachite-assistant-surface-foreground')
                        .trim() || defaultTheme.colors.assistant.surfaceForeground,
            },
            table: {
                background:
                    computedStyle.getPropertyValue('--malachite-table-background').trim() ||
                    defaultTheme.colors.table.background,
                boxShadow:
                    computedStyle.getPropertyValue('--malachite-table-box-shadow').trim() ||
                    defaultTheme.colors.table.boxShadow,
                header: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-table-header-background')
                            .trim() || defaultTheme.colors.table.header.background,
                    foreground:
                        computedStyle
                            .getPropertyValue('--malachite-table-header-foreground')
                            .trim() || defaultTheme.colors.table.header.foreground,
                    border:
                        computedStyle.getPropertyValue('--malachite-table-header-border').trim() ||
                        defaultTheme.colors.table.header.border,
                },
                row: {
                    background:
                        computedStyle.getPropertyValue('--malachite-table-row-background').trim() ||
                        defaultTheme.colors.table.row.background,
                    foreground:
                        computedStyle.getPropertyValue('--malachite-table-row-foreground').trim() ||
                        defaultTheme.colors.table.row.foreground,
                    hoverBackground:
                        computedStyle
                            .getPropertyValue('--malachite-table-row-hover-background')
                            .trim() || defaultTheme.colors.table.row.hoverBackground,
                    border:
                        computedStyle.getPropertyValue('--malachite-table-row-border').trim() ||
                        defaultTheme.colors.table.row.border,
                    selectedBackground:
                        computedStyle
                            .getPropertyValue('--malachite-table-row-selected-background')
                            .trim() || defaultTheme.colors.table.row.selectedBackground,
                    selectedAccent:
                        computedStyle
                            .getPropertyValue('--malachite-table-row-selected-accent')
                            .trim() || defaultTheme.colors.table.row.selectedAccent,
                    selectedForeground:
                        computedStyle
                            .getPropertyValue('--malachite-table-row-selected-foreground')
                            .trim() || defaultTheme.colors.table.row.selectedForeground,
                },
                border:
                    computedStyle.getPropertyValue('--malachite-table-border').trim() ||
                    defaultTheme.colors.table.border,
                borderedRow: {
                    background:
                        computedStyle
                            .getPropertyValue('--malachite-table-borderedRow-background')
                            .trim() || defaultTheme.colors.table.borderedRow.background,
                    outline:
                        computedStyle
                            .getPropertyValue('--malachite-table-borderedRow-outline')
                            .trim() || defaultTheme.colors.table.borderedRow.outline,
                },
                rowVariant: {
                    success: {
                        background:
                            computedStyle
                                .getPropertyValue(
                                    '--malachite-table-row-variant-success-background',
                                )
                                .trim() || defaultTheme.colors.table.rowVariant.success.background,
                    },
                    warning: {
                        background:
                            computedStyle
                                .getPropertyValue(
                                    '--malachite-table-row-variant-warning-background',
                                )
                                .trim() || defaultTheme.colors.table.rowVariant.warning.background,
                    },
                    error: {
                        background:
                            computedStyle
                                .getPropertyValue('--malachite-table-row-variant-error-background')
                                .trim() || defaultTheme.colors.table.rowVariant.error.background,
                    },
                    info: {
                        background:
                            computedStyle
                                .getPropertyValue('--malachite-table-row-variant-info-background')
                                .trim() || defaultTheme.colors.table.rowVariant.info.background,
                    },
                },
                error: {
                    iconBackground:
                        computedStyle
                            .getPropertyValue('--malachite-table-error-icon-background')
                            .trim() || defaultTheme.colors.table.error.iconBackground,
                    iconForeground:
                        computedStyle
                            .getPropertyValue('--malachite-table-error-icon-foreground')
                            .trim() || defaultTheme.colors.table.error.iconForeground,
                    titleForeground:
                        computedStyle
                            .getPropertyValue('--malachite-table-error-title-foreground')
                            .trim() || defaultTheme.colors.table.error.titleForeground,
                    subtitleForeground:
                        computedStyle
                            .getPropertyValue('--malachite-table-error-subtitle-foreground')
                            .trim() || defaultTheme.colors.table.error.subtitleForeground,
                },
            },
        },
    };
}

let themeInitialized = false;

/**
 * Applies the document-level theme once per page. Called from ShadowRootWrapper rather than
 * run at import: a bare top-level call is an import-time side effect, and `sideEffects: false`
 * in package.json tells a consumer's bundler it may drop a module nothing imports from, which
 * would take the theme with it. A consumer wanting the document themed without mounting any
 * Malachite component calls `initializeTheme()` directly.
 */
export const ensureThemeInitialized = (): void => {
    if (themeInitialized) {
        return;
    }
    themeInitialized = true;
    initializeTheme();
};
